import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountBalance as TaxIcon,
  TrendingUp as ChartIcon,
  FamilyRestroom as FamilyIcon,
  Euro as EuroIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Savings as SavingsIcon,
  Home as MortgageIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { api } from '../contexts/ApiContext';
import { useSearchParams } from 'react-router-dom';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

const OMAR_COLOR = '#1976d2'; // Blue – avoid red (reserved for negative figures)
const HEIDI_COLOR = '#26a69a'; // Teal - softer than dark green
const S1_HEIDI_COLOR = '#26a69a';
const OMAR_BURNT_ORANGE = '#ef6c00';  // Burnt orange – used for Omar across all scenarios
const S1_OMAR_COLOR = OMAR_BURNT_ORANGE;
const S2_HEIDI_COLOR = '#7e57c2'; // Soft purple – distinct from S1
const S2_OMAR_COLOR = OMAR_BURNT_ORANGE;
const S3_HEIDI_COLOR = '#00897b'; // Teal – distinct from S1/S2
const S3_OMAR_COLOR = OMAR_BURNT_ORANGE;

const buildSourceRowsFromIncomes = (familyIncomes: any[]) => {
  const bySource = new Map<string, { heidi: number; omar: number; details: string }>();
  familyIncomes.forEach((income: any) => {
    if (!income.person) return;
    const name = income.income_name || income.incomeName || 'Unknown';
    if (!bySource.has(name)) bySource.set(name, { heidi: 0, omar: 0, details: name });
    const row = bySource.get(name)!;
    const amt = income.amount_yearly ?? 0;
    if (income.person.name?.toLowerCase().includes('heidi')) row.heidi += amt;
    else if (income.person.name?.toLowerCase().includes('omar')) row.omar += amt;
  });
  const rowOrder = ['lucena', 'rotunda', 'enable', 'apa', 'abberley', '19abb', '51lc', '87hp', 'from rrltd', 'from omheprops', 'omhe'];
  return Array.from(bySource.entries())
    .map(([k, v]) => ({ key: k, ...v }))
    .sort((a, b) => {
      const ai = rowOrder.findIndex(o => a.details.toLowerCase().includes(o));
      const bi = rowOrder.findIndex(o => b.details.toLowerCase().includes(o));
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
};

/** Renders bar label: prefer inside bar to maximize usable width; fall back to outside for tiny bars/€0. */
const BarLabelContent = (props: { x?: number; y?: number; width?: number; height?: number; value?: unknown; formatter?: (v: number) => string }) => {
  const { x = 0, y = 0, width = 0, height = 0, value, formatter } = props;
  const numVal = typeof value === 'number' ? value : NaN;
  const barWidth = (width as number) || 0;
  const minBarWidthForInsideLabel = 60;
  let text: string;
  if (numVal === 0) {
    text = '€0';
  } else if (formatter && numVal > 0) {
    text = formatter(numVal);
  } else {
    text = '';
  }
  if (!text) return null;
  const isZero = numVal === 0;
  const isInside = !isZero && barWidth >= minBarWidthForInsideLabel;
  const posX = isZero
    ? (x as number) + 4
    : isInside
      ? (x as number) + barWidth - 6
      : (x as number) + barWidth + 4;
  return (
    <text
      x={posX}
      y={(y as number) + (height as number) / 2}
      fontSize={13}
      fill={isInside ? '#fff' : '#000'}
      textAnchor={isZero ? 'start' : isInside ? 'end' : 'start'}
      dominantBaseline="middle"
    >
      {text}
    </text>
  );
};

/** Custom Y-axis tick: compact text so bars get more horizontal room */
const IncomeSourceYAxisTick = (props: { x?: number; y?: number; payload?: { value: string } }) => {
  const { x = 0, y = 0, payload } = props;
  const text = payload?.value ?? '';
  if (!text) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} textAnchor="end" dominantBaseline="middle" fontSize={13} fontWeight={500} fill="#333">
        {text}
      </text>
    </g>
  );
};

/** Stable empty array to avoid pension-relief effect loop when pensions are undefined */
const EMPTY_PENSIONS: Array<{ id?: number; name: string; heidi: number; omar: number; totalContributions: number; currentValue: number }> = [];

/** Single source of truth: is this income rental? Used for type label and rental/non-rental totals. */
const isRentalIncomeType = (inc: { income_name?: string; incomeName?: string }): boolean => {
  const n = ((inc.income_name ?? inc.incomeName) ?? '').toString().toLowerCase();
  return n.includes('rental') || n.includes(' rent') || n.includes('51lc');
};

/** Client-side filters for the Income Sources chart/table (details, key, chart label). */
function filterIncomeSourceRowsForDisplay<
  T extends { key: string; details: string; type?: string; heidi: number; omar: number }
>(rows: T[], search: string, kind: 'all' | 'rental' | 'non-rental'): T[] {
  let out = [...rows];
  const q = search.trim().toLowerCase();
  if (q) {
    out = out.filter((r) => {
      const d = (r.details || '').toLowerCase();
      const k = (r.key || '').toLowerCase();
      const chartLabel = getDetailsDisplayForChart(r.details).toLowerCase();
      return d.includes(q) || k.includes(q) || chartLabel.includes(q);
    });
  }
  if (kind === 'rental') {
    out = out.filter(
      (r) => r.type === 'Rental Income' || isRentalIncomeType({ income_name: r.details })
    );
  } else if (kind === 'non-rental') {
    out = out.filter(
      (r) => !(r.type === 'Rental Income' || isRentalIncomeType({ income_name: r.details }))
    );
  }
  return out;
}

const getDetailsDisplayForChart = (name: string) => {
  if (!name) return '';
  const lower = name.toLowerCase();
  if (lower.includes('abberley') || lower.includes('19abb')) return '19Abb rental';
  if (lower.includes('from rrltd')) return 'PAYE RRLtd';
  if (lower.includes('from omheprops') || lower.includes('omheprops')) return 'PAYE OmHeProps';
  if (lower.includes('enable') && (lower.includes('assesment') || lower.includes('assessment') || lower.includes('laptop'))) return 'Enable Laptop';
  if (lower.includes('87hp') && lower.includes('rental')) return '87HP rental';
  if (lower.includes('51lc')) return '51LC rent';
  if (lower.includes('lucena') || lower.includes('rotunda')) return 'Lucena/Rotunda';
  if (lower.includes('apa')) return 'APA Private Work';
  return name.length > 16 ? name.slice(0, 14) + '…' : name;
};

interface Loan {
  id: number;
  loan_name: string;
  lender: string;
  loan_type: string;
  monthly_payment: number;
}

interface Person {
  id: number;
  name: string;
  family_id: number;
  relationship: string;
}

interface Family {
  id: number;
  name: string;
  members: Person[];
}

interface Income {
  id: number;
  person_id: number;
  amount_yearly: number;
  amount_monthly?: number;
  source: string;
  income_type: string;
  income_name?: string;
  person?: Person;
}

interface PersonIncomeSummary {
  person: Person;
  family: Family;
  payeInternal: number;
  payeExternal: number;
  otherInternal: number;
  otherExternal: number;
  totalIncome: number;
}

interface FamilyIncomeSummary {
  family: Family;
  people: PersonIncomeSummary[];
  totalFamilyIncome: number;
}

interface TaxCalculation {
  familyId: number;
  familyName: string;
  totalIncome: number;
  rentalIncome: number;
  earnedIncome: number;
  /** For married two earners: per-person incomes for pension/offset calculations */
  earner1Income?: number;
  earner2Income?: number;
  taxableIncome: number;
  payeTax: number;
  uscTax: number;
  prsiTax: number;
  totalTax: number;
  netIncome: number;
  taxRate: number;
  breakdown: {
    payeBands: Array<{ band: string; rate: number; amount: number; tax: number }>;
    uscBands: Array<{ band: string; rate: number; amount: number; tax: number }>;
    prsi: { threshold: number; rate: number; taxableAmount: number; tax: number };
    /** For married two earners: explicit per-person PAYE rows */
    payeTwoEarnerRows?: Array<{ person: string; band: string; rate: number; amount: number; tax: number }>;
    /** Tax before credits; used for Pre-Credits Total */
    payeBeforeCredits?: number;
    /** Breakdown of tax credits (e.g. Married Personal, PAYE×2) */
    taxCreditsBreakdown?: Array<{ label: string; amount: number }>;
  };
}

export const Tax: React.FC = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  // Charts: maximize plot area width while keeping labels readable.
  const chartLeftPad = isSmDown ? 90 : isMdDown ? 105 : 120;
  const chartRightPad = 0;
  const incomeSourceYAxisWidth = isSmDown ? 100 : isMdDown ? 110 : 120;
  const incomeSourceChartLeftPad = isSmDown ? 8 : 12;

  const [families, setFamilies] = useState<Family[]>([]);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [taxCalculations, setTaxCalculations] = useState<TaxCalculation[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(2025);
  const [pensionAgeBand, setPensionAgeBand] = useState<string>('40-49');
  const [showFilters, setShowFilters] = useState(false);
  /** Income Sources section: filter chart/table rows (not persisted). */
  const [incomeSourceSearch, setIncomeSourceSearch] = useState('');
  const [incomeTypeFilter, setIncomeTypeFilter] = useState<'all' | 'rental' | 'non-rental'>('all');
  const [taxConfigByYear, setTaxConfigByYear] = useState<Record<number, any>>({});
  const [pensionConfig, setPensionConfig] = useState<{ ageBands: Record<string, number>; incomeCap: number } | null>(null);
  const [taxConfigLoading, setTaxConfigLoading] = useState(true);
  const [pensionConfigLoading, setPensionConfigLoading] = useState(true);
  const [pensionReliefResult, setPensionReliefResult] = useState<{
    maxRelievable: number; reliefAmount: number; payeRelief: number; payeReliefAtMax: number;
    amountIn20Band: number; amountIn40Band: number; maxByAge?: number;
  } | null>(null);
  const [taxInfoTab, setTaxInfoTab] = useState(0); // 0 = 2026 (default), 1 = 2025, 2 = 2024
  const [taxInfoModalOpen, setTaxInfoModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [familyTab, setFamilyTabState] = useState(0);
  const [scenarioTab, setScenarioTabState] = useState(0); // 0=Comparison, 1=Scenario 1, 2=Scenario 2, 3=Scenario 3

  const SCENARIO_KEYS = ['comparison', 'scenario1', 'scenario2', 'scenario3'] as const;
  const setFamilyTab = useCallback((v: number) => {
    setFamilyTabState(v);
    setScenarioTabState(0);
    const next = new URLSearchParams(searchParams);
    next.set('family', String(v));
    next.set('scenario', SCENARIO_KEYS[0]);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const setScenarioTab = useCallback((v: number) => {
    setScenarioTabState(v);
    const next = new URLSearchParams(searchParams);
    next.set('scenario', SCENARIO_KEYS[v]);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [scenarioData1, setScenarioData1] = useState<{
    incomes: Income[];
    incomeSummary?: { sourceRows: any[]; totalHeidi: number; totalOmar: number; totalCombined: number; totalRental: number; totalNonRental: number; totalRrltd: number } | null;
    pensions: Array<{ id?: number; name: string; heidi: number; omar: number; totalContributions: number; currentValue: number }>;
    loans: Loan[];
    scenarioId?: number;
    description?: string;
  } | null>(null);
  const [scenarioData2, setScenarioData2] = useState<{
    incomes: Income[];
    incomeSummary?: { sourceRows: any[]; totalHeidi: number; totalOmar: number; totalCombined: number; totalRental: number; totalNonRental: number; totalRrltd: number } | null;
    pensions: Array<{ id?: number; name: string; heidi: number; omar: number; totalContributions: number; currentValue: number }>;
    loans: Loan[];
    scenarioId?: number;
    description?: string;
  } | null>(null);
  const [scenarioData3, setScenarioData3] = useState<{
    incomes: Income[];
    incomeSummary?: { sourceRows: any[]; totalHeidi: number; totalOmar: number; totalCombined: number; totalRental: number; totalNonRental: number; totalRrltd: number } | null;
    pensions: Array<{ id?: number; name: string; heidi: number; omar: number; totalContributions: number; currentValue: number }>;
    loans: Loan[];
    scenarioId?: number;
    description?: string;
  } | null>(null);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [scenarioData3Error, setScenarioData3Error] = useState(false);
  const [scenarioDescEditOpen, setScenarioDescEditOpen] = useState(false);
  const [scenarioDescEditValue, setScenarioDescEditValue] = useState('');
  const [scenarioDescSaving, setScenarioDescSaving] = useState(false);
  const [familyIncomeSummaries, setFamilyIncomeSummaries] = useState<FamilyIncomeSummary[]>([]);
  const [incomeEditOpen, setIncomeEditOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<PersonIncomeSummary | null>(null);
  const [incomeFormData, setIncomeFormData] = useState({
    payeInternal: '',
    payeExternal: '',
    otherInternal: '',
    otherExternal: ''
  });
  const [incomeEditMode, setIncomeEditMode] = useState<'monthly' | 'yearly'>('monthly');
  const [incomeSubmitting, setIncomeSubmitting] = useState(false);
  const [incomeSourceEditOpen, setIncomeSourceEditOpen] = useState(false);
  const [incomeSourceDeleteOpen, setIncomeSourceDeleteOpen] = useState(false);
  const [editingIncomeRecords, setEditingIncomeRecords] = useState<Income[]>([]);
  const [incomeToDeleteRecords, setIncomeToDeleteRecords] = useState<Income[]>([]);
  const [incomeSourceActionLoading, setIncomeSourceActionLoading] = useState(false);
  const [incomeSourceEditForm, setIncomeSourceEditForm] = useState<{
    heidiAmount: number; omarAmount: number; type: string; sourceEmployer: string;
    internalExternal: string; details: string;
  } | null>(null);
  const [incomeAddOpen, setIncomeAddOpen] = useState(false);
  const [incomeAddFamilyId, setIncomeAddFamilyId] = useState<number | null>(null);
  const [incomeAddForm, setIncomeAddForm] = useState({
    heidiAmount: 0, omarAmount: 0, type: 'PAYE', sourceEmployer: 'Lucena',
    internalExternal: 'External', details: ''
  });
  /** Scenario 1 data per family (for pensions when not in OmHe scenario mode) - from API */
  const [scenarioDataByFamily, setScenarioDataByFamily] = useState<Record<number, {
    pensions: Array<{ id?: number; name: string; heidi: number; omar: number; totalContributions: number; currentValue: number }>;
    scenarioId?: number;
  }>>({});
  /** Backend-computed income summaries (sourceRows, totalRental, etc.) per family - from GET /api/income/summary */
  const [incomeSummaryByFamily, setIncomeSummaryByFamily] = useState<Record<number, {
    sourceRows: Array<{ key: string; details: string; heidi: number; omar: number; type: string; records: any[] }>;
    totalHeidi: number; totalOmar: number; totalCombined: number;
    totalRental: number; totalNonRental: number; totalRrltd: number;
  }>>({});
  const [recommendationsMap, setRecommendationsMap] = useState<Record<string, {
    type: string; title: string; paragraphs: string[];
    savingAmount?: number; bestShift?: number; effectiveRateBefore?: number; effectiveRateAfter?: number;
  }>>({});
  const [pensionAddOpen, setPensionAddOpen] = useState(false);
  const [pensionAddForm, setPensionAddForm] = useState({ name: '', heidi: 0, omar: 0, totalContributions: 0, currentValue: 0 });
  const [pensionEditOpen, setPensionEditOpen] = useState(false);
  const [pensionEditIdx, setPensionEditIdx] = useState<number | null>(null);
  const [pensionEditForm, setPensionEditForm] = useState<{ name: string; heidi: number; omar: number; totalContributions: number; currentValue: number } | null>(null);
  const [pensionDeleteOpen, setPensionDeleteOpen] = useState(false);
  const [pensionDeleteIdx, setPensionDeleteIdx] = useState<number | null>(null);
  const [pensionActionLoading, setPensionActionLoading] = useState(false);
  const [pensionEditId, setPensionEditId] = useState<number | null>(null);
  const [pensionDeleteId, setPensionDeleteId] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Sync URL -> state on mount and when URL changes
  useEffect(() => {
    const familyParam = searchParams.get('family');
    const scenarioParam = searchParams.get('scenario');
    const familyIdx = familyParam !== null ? parseInt(familyParam, 10) : NaN;
    const scenarioIdx = scenarioParam ? SCENARIO_KEYS.indexOf(scenarioParam as typeof SCENARIO_KEYS[number]) : -1;
    const maxFamily = Math.max(0, taxCalculations.length - 1);
    if (!isNaN(familyIdx) && familyIdx >= 0 && familyIdx <= maxFamily) setFamilyTabState(familyIdx);
    if (scenarioIdx >= 0) setScenarioTabState(scenarioIdx);
  }, [searchParams, taxCalculations.length]);

  // Set URL when no params (initial load) so each tab has a URL
  useEffect(() => {
    if (searchParams.get('family') === null && taxCalculations.length > 0) {
      const next = new URLSearchParams(searchParams);
      next.set('family', String(Math.min(familyTab, taxCalculations.length - 1)));
      next.set('scenario', SCENARIO_KEYS[scenarioTab]);
      setSearchParams(next, { replace: true });
    }
  }, [taxCalculations.length, familyTab, scenarioTab, searchParams, setSearchParams]);

  useEffect(() => {
    const years = [2024, 2025, 2026];
    const toFetch = years.filter(y => !taxConfigByYear[y]);
    if (toFetch.length === 0) {
      setTaxConfigLoading(false);
      return;
    }
    let mounted = true;
    setTaxConfigLoading(true);
    (async () => {
      for (const y of toFetch) {
        try {
          const r = await api.get(`/api/tax-config?year=${y}`);
          if (mounted && r.data?.config) setTaxConfigByYear(prev => ({ ...prev, [y]: r.data.config }));
        } catch (_) { /* ignore */ }
      }
      if (mounted) setTaxConfigLoading(false);
    })();
    return () => { mounted = false; };
  }, [taxConfigByYear]);

  useEffect(() => {
    let mounted = true;
    api.get('/api/pension-config').then(r => {
      if (mounted && r.data) setPensionConfig({ ageBands: r.data.ageBands || {}, incomeCap: r.data.incomeCap ?? 115000 });
    }).catch(() => { if (mounted) setPensionConfig(null); }).finally(() => { if (mounted) setPensionConfigLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get('/api/tax-preferences').then(r => {
      if (mounted && r.data) {
        setPensionAgeBand(r.data.pensionAgeBand ?? '40-49');
        setSelectedYear(r.data.selectedYear ?? 2025);
      }
    }).catch(() => { /* use defaults */ });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (families.length > 0 && incomes.length > 0) {
      processIncomeData(incomes, families);
    }
  }, [families, incomes]);

  const fetchTaxCalculations = useCallback(async () => {
    if (!taxConfigByYear[selectedYear] || families.length === 0 || incomes.length === 0) return;
    const isOmHe = (n: string) => n === 'OmHe' || n === 'Omar and Heidi';
    const familyIncomesMap: Record<string, Income[]> = {};
    const familiesWithIncome: Family[] = [];
    for (const family of families) {
      const activeScenario = scenarioTab === 1 ? scenarioData1 : scenarioTab === 2 ? scenarioData2 : scenarioTab === 3 ? scenarioData3 : null;
      const famIncomes = (isOmHe(family.name || '') && activeScenario?.incomes?.length)
        ? activeScenario.incomes
        : incomes.filter(inc => inc.person && inc.person.family_id === family.id);
      if (famIncomes.length > 0) {
        familyIncomesMap[String(family.id)] = famIncomes;
        familiesWithIncome.push(family);
      }
    }
    if (familiesWithIncome.length === 0) {
      setTaxCalculations([]);
      setRecommendationsMap({});
      return;
    }
    try {
      const res = await api.post('/api/tax-calculate', {
        year: selectedYear,
        families: familiesWithIncome.map(f => ({ id: f.id, name: f.name })),
        familyIncomesMap: Object.fromEntries(
          Object.entries(familyIncomesMap).map(([k, v]) => [
            k,
            v.map(i => ({
              amount_yearly: i.amount_yearly,
              income_name: i.income_name ?? (i as any).incomeName,
              person: i.person ? { id: i.person.id, name: i.person.name } : undefined
            }))
          ])
        )
      });
      setTaxCalculations(res.data.calculations || []);
      setRecommendationsMap(res.data.recommendationsMap || {});
    } catch (err: any) {
      console.error('Tax calculate error:', err);
      setTaxCalculations([]);
      setRecommendationsMap({});
    }
  }, [families, incomes, selectedYear, scenarioTab, scenarioData1, scenarioData2, scenarioData3, taxConfigByYear]);

  useEffect(() => {
    fetchTaxCalculations();
  }, [fetchTaxCalculations]);

  useEffect(() => {
    if (families.length === 0) return;
    const familiesWithIncome = families.filter(f =>
      incomes.some(inc => inc.person && inc.person.family_id === f.id)
    );
    if (familiesWithIncome.length === 0) return;
    let mounted = true;
    Promise.all(
      familiesWithIncome.map(f =>
        api.get('/api/tax-scenario/', { params: { family_id: f.id, scenario: 1 } })
          .then(r => ({ familyId: f.id, data: r.data }))
      )
    ).then(results => {
      if (!mounted) return;
      const map: Record<number, { pensions: any[]; scenarioId?: number }> = {};
      results.forEach(({ familyId, data }) => {
        map[familyId] = {
          pensions: (data.pensions || []).map((p: any) => ({
            id: p.id,
            name: p.name,
            heidi: p.heidi ?? 0,
            omar: p.omar ?? 0,
            totalContributions: p.totalContributions ?? p.total_contributions ?? 0,
            currentValue: p.currentValue ?? p.current_value ?? 0
          })),
          scenarioId: data.scenario?.id
        };
      });
      setScenarioDataByFamily(prev => ({ ...prev, ...map }));
    }).catch(() => {});
    return () => { mounted = false; };
  }, [families, incomes]);

  // Fetch backend income summaries for each family (sourceRows, totalRental, etc.)
  useEffect(() => {
    if (families.length === 0) return;
    let mounted = true;
    Promise.all(
      families.map(f =>
        api.get('/api/income/summary', { params: { family_id: f.id } })
          .then(r => ({ familyId: f.id, data: r.data }))
          .catch(() => ({ familyId: f.id, data: null }))
      )
    ).then(results => {
      if (!mounted) return;
      const map: Record<number, any> = {};
      results.forEach(({ familyId, data }) => {
        if (data?.sourceRows != null) map[familyId] = data;
      });
      setIncomeSummaryByFamily(prev => ({ ...prev, ...map }));
    });
    return () => { mounted = false; };
  }, [families, incomes]);

  const isOmHeFamily = (familyName: string) =>
    familyName === 'OmHe' || familyName === 'Omar and Heidi';
  const safeFamilyTab = taxCalculations.length > 0 ? Math.min(familyTab, taxCalculations.length - 1) : 0;
  const currentCalc = taxCalculations[safeFamilyTab];
  const showScenarioTabs = currentCalc && isOmHeFamily(currentCalc.familyName || '');

  useEffect(() => {
    if (showScenarioTabs && currentCalc) {
      fetchBothScenarios(currentCalc.familyId);
    } else {
      setScenarioData1(null);
      setScenarioData2(null);
      setScenarioData3(null);
      setScenarioData3Error(false);
      setScenarioLoading(false);
    }
  }, [showScenarioTabs, currentCalc?.familyId]);

  // Derive scenarioData from tab and fetched data
  const effectiveScenarioData = scenarioTab === 1 ? scenarioData1 : scenarioTab === 2 ? scenarioData2 : scenarioTab === 3 ? scenarioData3 : null;
  const effectivePensions = (showScenarioTabs && effectiveScenarioData)
    ? effectiveScenarioData.pensions
    : (currentCalc ? (scenarioDataByFamily[currentCalc.familyId]?.pensions ?? EMPTY_PENSIONS) : EMPTY_PENSIONS);

  useEffect(() => {
    if (!currentCalc || !taxConfigByYear[selectedYear]) {
      setPensionReliefResult(null);
      return;
    }
    const totalPension = effectivePensions.reduce((s: number, r: any) => s + (r.heidi ?? 0) + (r.omar ?? 0), 0);
    let mounted = true;
    api.post('/api/pension-relief', {
      year: selectedYear,
      totalIncome: currentCalc.totalIncome,
      earnedIncome: currentCalc.earnedIncome,
      totalPension,
      pensionAgeBand,
      earner1Income: currentCalc.earner1Income,
      earner2Income: currentCalc.earner2Income,
      familyName: currentCalc.familyName,
    }).then(r => { if (mounted) setPensionReliefResult(r.data); })
      .catch(() => { if (mounted) setPensionReliefResult(null); });
    return () => { mounted = false; };
  }, [currentCalc, selectedYear, pensionAgeBand, effectivePensions]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch families, incomes, and loans
      const [familiesResponse, incomesResponse, loansResponse] = await Promise.all([
        api.get('/api/families/'),
        api.get('/api/income/'),
        api.get('/api/loans/')
      ]);
      
      setFamilies(familiesResponse.data.families || []);
      const incomeList = incomesResponse.data.incomes ?? incomesResponse.data.income ?? [];
      setIncomes(Array.isArray(incomeList) ? incomeList : []);
      setLoans(loansResponse.data.loans || []);
    } catch (err: any) {
      console.error('Error fetching tax data:', err);
      setError(err.response?.data?.error || 'Failed to load tax data');
    } finally {
      setLoading(false);
    }
  };

  const mapScenarioResponse = (data: any) => ({
    incomes: data.incomes || [],
    incomeSummary: data.incomeSummary || null,
    description: data.scenario?.description ?? '',
    pensions: (data.pensions || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      heidi: p.heidi ?? 0,
      omar: p.omar ?? 0,
      totalContributions: p.totalContributions ?? p.total_contributions ?? 0,
      currentValue: p.currentValue ?? p.current_value ?? 0
    })),
    loans: data.loans || [],
    scenarioId: data.scenario?.id
  });

  const fetchScenarioData = async (familyId: number, scenario: number) => {
    try {
      setScenarioLoading(true);
      const res = await api.get('/api/tax-scenario/', {
        params: { family_id: familyId, scenario }
      });
      const mapped = mapScenarioResponse(res.data);
      if (scenario === 1) setScenarioData1(mapped);
      else if (scenario === 2) setScenarioData2(mapped);
      else setScenarioData3(mapped);
    } catch (err: any) {
      console.error('Error fetching scenario data:', err);
      if (scenario === 1) setScenarioData1(null);
      else if (scenario === 2) setScenarioData2(null);
      else setScenarioData3(null);
    } finally {
      setScenarioLoading(false);
    }
  };

  const fetchBothScenarios = async (familyId: number) => {
    setScenarioLoading(true);
    setScenarioData3Error(false);
    const debug = searchParams.get('debug') ? 1 : undefined;
    try {
      await api.post('/api/tax-scenario/sync-pensions-to-max', null, { params: { family_id: familyId } });
    } catch (err: any) {
      console.warn('Sync pensions to max failed (non-fatal):', err?.response?.data?.error || err);
    }
    const [s1, s2, s3] = await Promise.allSettled([
      api.get('/api/tax-scenario/', { params: { family_id: familyId, scenario: 1, ...(debug && { debug: 1 }) } }),
      api.get('/api/tax-scenario/', { params: { family_id: familyId, scenario: 2, ...(debug && { debug: 1 }) } }),
      api.get('/api/tax-scenario/', { params: { family_id: familyId, scenario: 3, ...(debug && { debug: 1 }) } })
    ]);
    if (s1.status === 'fulfilled') setScenarioData1(mapScenarioResponse(s1.value.data));
    else {
      const err = s1.reason;
      console.error('Scenario 1 fetch failed:', err?.response?.data?.error || err);
      if (err?.response?.data?.traceback) console.error('Traceback:', err.response.data.traceback);
      setScenarioData1(null);
    }
    if (s2.status === 'fulfilled') setScenarioData2(mapScenarioResponse(s2.value.data));
    else {
      const err = s2.reason;
      console.error('Scenario 2 fetch failed:', err?.response?.data?.error || err);
      if (err?.response?.data?.traceback) console.error('Traceback:', err.response.data.traceback);
      setScenarioData2(null);
    }
    if (s3.status === 'fulfilled') {
      setScenarioData3(mapScenarioResponse(s3.value.data));
      setScenarioData3Error(false);
    } else {
      const err = s3.reason;
      console.error('Scenario 3 fetch failed:', err?.response?.data?.error || err);
      if (err?.response?.data?.traceback) console.error('Traceback:', err.response.data.traceback);
      setScenarioData3(null);
      setScenarioData3Error(true);
    }
    setScenarioLoading(false);
  };

  const processIncomeData = (incomesData: Income[], familiesData: Family[]) => {
    const personIncomeMap = new Map<number, PersonIncomeSummary>();
    incomesData.forEach(income => {
      if (!income.person) return;
      const personId = income.person.id;
      const family = familiesData.find(f => f.id === income.person!.family_id);
      if (!personIncomeMap.has(personId)) {
        personIncomeMap.set(personId, {
          person: income.person,
          family: family || { id: income.person.family_id, name: 'Unknown', members: [] },
          payeInternal: 0,
          payeExternal: 0,
          otherInternal: 0,
          otherExternal: 0,
          totalIncome: 0
        });
      }
      const personSummary = personIncomeMap.get(personId)!;
      const amount = income.amount_yearly;
      if (income.income_type === 'PAYE' && income.source === 'Internal') personSummary.payeInternal += amount;
      else if (income.income_type === 'PAYE' && income.source === 'External') personSummary.payeExternal += amount;
      else if (income.income_type === 'Other' && income.source === 'Internal') personSummary.otherInternal += amount;
      else if (income.income_type === 'Other' && income.source === 'External') personSummary.otherExternal += amount;
      personSummary.totalIncome += amount;
    });
    const familyMap = new Map<number, FamilyIncomeSummary>();
    personIncomeMap.forEach(personSummary => {
      const familyId = personSummary.family.id;
      if (!familyMap.has(familyId)) {
        familyMap.set(familyId, { family: personSummary.family, people: [], totalFamilyIncome: 0 });
      }
      const familySummary = familyMap.get(familyId)!;
      familySummary.people.push(personSummary);
      familySummary.totalFamilyIncome += personSummary.totalIncome;
    });
    setFamilyIncomeSummaries(Array.from(familyMap.values()));
  };

  const retryApiCall = async (apiCall: () => Promise<any>, maxRetries = 3): Promise<any> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall();
      } catch (err: any) {
        if (attempt === maxRetries) throw err;
        await new Promise(resolve => setTimeout(resolve, attempt * 1000));
      }
    }
  };

  const handleIncomeEdit = (personSummary: PersonIncomeSummary) => {
    setEditingPerson(personSummary);
    setIncomeFormData({
      payeInternal: personSummary.payeInternal.toString(),
      payeExternal: personSummary.payeExternal.toString(),
      otherInternal: personSummary.otherInternal.toString(),
      otherExternal: personSummary.otherExternal.toString()
    });
    setIncomeEditOpen(true);
  };

  const handleIncomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPerson || incomeSubmitting) return;
    try {
      setIncomeSubmitting(true);
      setError('');
      const personIncomes = incomes.filter(income => income.person?.id === editingPerson.person.id);
      const incomeUpdates = [
        { source: 'Internal', income_type: 'PAYE', amount: parseFloat(incomeFormData.payeInternal) || 0 },
        { source: 'External', income_type: 'PAYE', amount: parseFloat(incomeFormData.payeExternal) || 0 },
        { source: 'Internal', income_type: 'Other', amount: parseFloat(incomeFormData.otherInternal) || 0 },
        { source: 'External', income_type: 'Other', amount: parseFloat(incomeFormData.otherExternal) || 0 }
      ];
      for (const income of personIncomes) {
        await retryApiCall(() => api.delete(`/api/income/${income.id}`));
      }
      for (const incomeData of incomeUpdates) {
        if (incomeData.amount > 0) {
          const monthlyAmount = incomeEditMode === 'monthly' ? incomeData.amount : incomeData.amount / 12;
          const yearlyAmount = incomeEditMode === 'yearly' ? incomeData.amount : incomeData.amount * 12;
          await retryApiCall(() => api.post('/api/income', {
            person_name: editingPerson.person.name,
            source: incomeData.source,
            income_type: incomeData.income_type,
            amount_monthly: monthlyAmount,
            amount_yearly: yearlyAmount
          }));
        }
      }
      setIncomeEditOpen(false);
      setEditingPerson(null);
      setIncomeFormData({ payeInternal: '', payeExternal: '', otherInternal: '', otherExternal: '' });
      await retryApiCall(() => fetchData());
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to save income: ${err.message || 'Unknown error'}`);
    } finally {
      setIncomeSubmitting(false);
    }
  };

  const handleIncomeEditClose = () => {
    setIncomeEditOpen(false);
    setEditingPerson(null);
    setIncomeFormData({ payeInternal: '', payeExternal: '', otherInternal: '', otherExternal: '' });
    setIncomeSubmitting(false);
  };

  const handleIncomeSourceDelete = async () => {
    if (incomeToDeleteRecords.length === 0) return;
    try {
      setIncomeSourceActionLoading(true);
      setError('');
      for (const income of incomeToDeleteRecords) {
        await retryApiCall(() => api.delete(`/api/income/${income.id}`));
      }
      setIncomeSourceDeleteOpen(false);
      setIncomeToDeleteRecords([]);
      await retryApiCall(() => fetchData());
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to delete income: ${err.message || 'Unknown error'}`);
    } finally {
      setIncomeSourceActionLoading(false);
    }
  };

  const getTypeLabelFromIncome = (inc: Income): string => {
    const name = ((inc as any).income_name ?? (inc as any).incomeName ?? '').toString().toLowerCase();
    if (name.includes('lucena') || name.includes('rotunda')) return 'Contractor';
    if (name.includes('enable')) return 'Sole Trader';
    if (name.includes('apa')) return 'Sole Trader';
    if (isRentalIncomeType(inc)) return 'Rental Income';
    if (name.includes('from rrltd') || name.includes('paye salary from rrltd')) return 'PAYE';
    if (name.includes('from omheprops') || name.includes('paye salary from omheprops')) return 'PAYE';
    if ((inc.income_type ?? 'Other') === 'PAYE') return 'PAYE';
    return 'Other';
  };

  const mapDisplayTypeToBackend = (displayType: string): string =>
    displayType === 'PAYE' ? 'PAYE' : 'Other';

  const SOURCE_OPTIONS = ['Lucena', 'Enable Ireland', 'APA', 'B2L Property', 'RRLtd', 'OmHeProps'];

  const handleIncomeAddSubmit = async () => {
    if (!incomeAddFamilyId) return;
    const family = families.find(f => f.id === incomeAddFamilyId);
    const people: Person[] = (family as any)?.members ?? (family as any)?.people ?? [];
    const heidi = people.find((p: Person) => p.name?.toLowerCase().includes('heidi'));
    const omar = people.find((p: Person) => p.name?.toLowerCase().includes('omar'));
    const person1 = heidi ?? people[0];
    const person2 = omar ?? people[1];
    if (!person1 && !person2) {
      setError('No family members found for this family');
      return;
    }
    const { heidiAmount, omarAmount, type, internalExternal, details } = incomeAddForm;
    if ((!person1 || heidiAmount <= 0) && (!person2 || omarAmount <= 0)) {
      setError('Enter at least one amount');
      return;
    }
    try {
      setIncomeSourceActionLoading(true);
      setError('');
      const createPayload = (personId: number, amount: number) => ({
        person_id: personId,
        amount_yearly: amount,
        amount_monthly: amount / 12,
        income_type: mapDisplayTypeToBackend(type),
        source: internalExternal,
        income_name: details || undefined
      });
      if (person1 && heidiAmount > 0) await retryApiCall(() => api.post('/api/income', createPayload(person1.id, heidiAmount)));
      if (person2 && omarAmount > 0) await retryApiCall(() => api.post('/api/income', createPayload(person2.id, omarAmount)));
      setIncomeAddOpen(false);
      setIncomeAddFamilyId(null);
      setIncomeAddForm({ heidiAmount: 0, omarAmount: 0, type: 'PAYE', sourceEmployer: 'Lucena', internalExternal: 'External', details: '' });
      await retryApiCall(() => fetchData());
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to add income: ${err.message || 'Unknown error'}`);
    } finally {
      setIncomeSourceActionLoading(false);
    }
  };

  const getSourceFromDetails = (details: string): string => {
    const lower = (details || '').toLowerCase();
    if (lower.includes('lucena') || lower.includes('rotunda')) return 'Lucena';
    if (lower.includes('enable')) return 'Enable Ireland';
    if (lower.includes('apa')) return 'APA';
    if (lower.includes('abberley') || lower.includes('19abb')) return 'B2L Property';
    if (lower.includes('87hp')) return 'RRLtd';
    if (lower.includes('rrltd') && !lower.includes('omhe')) return 'RRLtd';
    if (lower.includes('omhe') || lower.includes('omheprops')) return 'OmHeProps';
    return SOURCE_OPTIONS[0];
  };

  const handleIncomeSourceEdit = (records: Income[]) => {
    setEditingIncomeRecords(records);
    let heidiAmount = 0, omarAmount = 0;
    const first = records[0];
    records.forEach(r => {
      const amt = r.amount_yearly ?? 0;
      if (r.person?.name?.toLowerCase().includes('heidi')) heidiAmount += amt;
      else if (r.person?.name?.toLowerCase().includes('omar')) omarAmount += amt;
    });
    setIncomeSourceEditForm({
      heidiAmount,
      omarAmount,
      type: getTypeLabelFromIncome(first),
      sourceEmployer: getSourceFromDetails(first?.income_name ?? ''),
      internalExternal: (first?.source === 'Internal' || (first?.income_name || '').toLowerCase().includes('87hp')) ? 'Internal' : 'External',
      details: first?.income_name ?? ''
    });
    setIncomeSourceEditOpen(true);
  };

  const handleIncomeSourceEditSubmit = async () => {
    if (!incomeSourceEditForm) return;
    try {
      setIncomeSourceActionLoading(true);
      setError('');
      const heidiRecord = editingIncomeRecords.find(r => r.person?.name?.toLowerCase().includes('heidi'));
      const omarRecord = editingIncomeRecords.find(r => r.person?.name?.toLowerCase().includes('omar'));
      const { heidiAmount, omarAmount, type, internalExternal, details } = incomeSourceEditForm;
      const payload = (amount: number) => ({
        amount_yearly: amount,
        amount_monthly: amount / 12,
        income_type: mapDisplayTypeToBackend(type),
        source: internalExternal,
        income_name: details || undefined
      });
      if (heidiRecord) await retryApiCall(() => api.put(`/api/income/${heidiRecord.id}`, payload(heidiAmount)));
      if (omarRecord) await retryApiCall(() => api.put(`/api/income/${omarRecord.id}`, payload(omarAmount)));
      setIncomeSourceEditOpen(false);
      setEditingIncomeRecords([]);
      setIncomeSourceEditForm(null);
      await retryApiCall(() => fetchData());
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to update income: ${err.message || 'Unknown error'}`);
    } finally {
      setIncomeSourceActionLoading(false);
    }
  };

  const calculateBandTax = (income: number, bands: Array<{ min: number; max: number; rate: number }>): number => {
    let totalTax = 0;
    
    for (const band of bands) {
      if (income <= band.min) break;
      
      const taxableAmount = Math.min(income, band.max) - band.min;
      totalTax += taxableAmount * band.rate;
    }
    
    return totalTax;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const computeScenarioSummary = useCallback((familyIncomes: any[], loans: Loan[], pensions?: { heidi: number; omar: number }[]) => {
    const taxRates = taxConfigByYear[selectedYear];
    if (!taxRates || !familyIncomes?.length) return null;
    const totalIncome = familyIncomes.reduce((s: number, i: any) => s + (i.amount_yearly ?? 0), 0);
    const rentalIncome = familyIncomes
      .filter((i: any) => isRentalIncomeType(i))
      .reduce((s: number, i: any) => s + (i.amount_yearly ?? 0), 0);
    const pensionAnnual = (pensions ?? []).reduce((s, p) => s + (p.heidi ?? 0) + (p.omar ?? 0), 0);
    const earnedIncome = totalIncome - rentalIncome;
    const earnerCount = new Set(familyIncomes.map((i: any) => i.person?.id).filter((id: any) => id != null)).size;
    const incomeByPerson = new Map<number, { name: string; amount: number }>();
    familyIncomes.forEach((inc: any) => {
      if (!inc.person) return;
      const id = inc.person.id;
      const amt = inc.amount_yearly ?? 0;
      if (!incomeByPerson.has(id)) incomeByPerson.set(id, { name: inc.person.name || 'Person', amount: 0 });
      incomeByPerson.get(id)!.amount += amt;
    });
    const personIncomes = Array.from(incomeByPerson.values()).filter(p => p.amount > 0);
    const [earner1, earner2] = personIncomes.length >= 2 ? personIncomes.sort((a, b) => a.amount - b.amount) : [personIncomes[0], null];
    const lowerIncome = earner1?.amount ?? 0;
    const higherIncome = earner2?.amount ?? 0;
    const uscTax = calculateBandTax(totalIncome, taxRates.USC);
    const prsiTaxableAmount = Math.max(0, totalIncome - taxRates.PRSI.threshold);
    const prsiTax = prsiTaxableAmount * taxRates.PRSI.employee;
    let payeTax: number;
    let amountIn40Band: number;
    if (earnerCount >= 2 && personIncomes.length >= 2) {
      // Ireland: primary (higher) earner €53k at 20%, secondary (lower) earner €35k at 20%
      const lowerBand = taxRates.PAYE.marriedTwoEarnersMaxTransfer ?? 35000;
      const higherBand = taxRates.PAYE.marriedTwoEarners?.[0]?.max ?? 53000;
      amountIn40Band = Math.max(0, lowerIncome - lowerBand) + Math.max(0, higherIncome - higherBand);
      payeTax = Math.min(lowerIncome, lowerBand) * 0.2 + Math.max(0, lowerIncome - lowerBand) * 0.4 +
        Math.min(higherIncome, higherBand) * 0.2 + Math.max(0, higherIncome - higherBand) * 0.4;
    } else {
      const bands = taxRates.PAYE.marriedOneEarner || taxRates.PAYE.single || [];
      const threshold20 = bands[0]?.max ?? 44000;
      amountIn40Band = Math.max(0, totalIncome - threshold20);
      payeTax = calculateBandTax(totalIncome, taxRates.PAYE.marriedOneEarner);
    }
    const taxCredits = earnerCount >= 2 ? taxRates.TAX_CREDITS.marriedTwoEarners : taxRates.TAX_CREDITS.marriedOneEarner;
    const finalPayeTax = Math.max(0, payeTax - taxCredits);
    const totalTax = finalPayeTax + uscTax + prsiTax;

    // Pension relief: reduces PAYE when contributing to pension
    const incomeCap = pensionConfig?.incomeCap ?? 115000;
    const agePct = pensionConfig?.ageBands?.[pensionAgeBand] ?? 0.25;
    const maxByAge = agePct * Math.min(earnedIncome, incomeCap);
    const maxRelievable = Math.min(earnedIncome, maxByAge);
    const reliefAmount = pensionAnnual > 0 ? Math.min(pensionAnnual, maxRelievable) : 0;
    const in40 = reliefAmount > 0 ? Math.min(reliefAmount, amountIn40Band) : 0;
    const in20 = reliefAmount > 0 ? Math.max(0, reliefAmount - amountIn40Band) : 0;
    const payeRelief = in40 * 0.40 + in20 * 0.20;

    // Net income after tax and pension: gross - tax - pension + pension relief
    const netIncome = totalIncome - totalTax - pensionAnnual + payeRelief;
    // Backend returns scenario-specific loans (AIB/Pepper filtered per scenario); use all mortgages
    const mortgageLoans = (loans || []).filter((l: Loan) => l.loan_type === 'mortgage');
    const mortgagesAnnual = mortgageLoans.reduce((s: number, l: Loan) => s + (l.monthly_payment ?? 0) * 12, 0);
    const disposableIncome = netIncome - mortgagesAnnual;
    const effectiveTaxRate = totalIncome > 0 ? (totalTax / totalIncome) * 100 : 0;
    const taxAfterPensionRelief = totalTax - payeRelief;
    return {
      totalIncome, rentalIncome, earnedIncome, pensionAnnual,
      payeTax: finalPayeTax, payeBeforeCredits: payeTax, taxCredits,
      uscTax, prsiTax, totalTax, taxAfterPensionRelief, netIncome, mortgagesAnnual, disposableIncome, effectiveTaxRate,
      mortgageLoans, payeRelief
    };
  }, [selectedYear, taxConfigByYear, pensionConfig, pensionAgeBand]);

  const getFamilyType = (familyName: string) => {
    const name = familyName || '';
    if (name === 'DwLe' || name === 'OmHe' || name === 'Omar and Heidi' || name === 'Dwayne and Lena') return 'Married Couple';
    if (name === 'SeCo' || name === 'Sean and Coral') return 'Single (Old Age Pensioner)';
    return 'Single';
  };

  const getFamilyDisplayName = (familyName: string) => {
    const displayNames: Record<string, string> = {
      'OmHe': 'Omar and Heidi',
      'DwLe': 'Dwayne and Lena',
      'SeCo': 'Sean and Coral'
    };
    return displayNames[familyName] || familyName;
  };

  if (loading) {
    return (
      <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!taxConfigLoading && !taxConfigByYear[selectedYear]) {
    return (
      <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 3 } }}>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Tax config for {selectedYear} is not available. Ensure the backend is running and the migration has been applied.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 3 } }}>
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Irish Tax Information">
              <IconButton
                onClick={() => setTaxInfoModalOpen(true)}
                size="medium"
                sx={{ mr: 0.5 }}
                aria-label="Irish Tax Information"
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h4" component="h1">
              Tax Calculations 🇮🇪
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<TaxIcon />}
              onClick={fetchData}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        {showFilters && (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Tax Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Tax Year"
                  onChange={(e) => {
                    const y = Number(e.target.value);
                    setSelectedYear(y);
                    api.put('/api/tax-preferences', { selectedYear: y }).catch(() => {});
                  }}
                >
                  <MenuItem value={2023}>2023</MenuItem>
                  <MenuItem value={2024}>2024</MenuItem>
                  <MenuItem value={2025}>2025</MenuItem>
                  <MenuItem value={2026}>2026</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Pension age band</InputLabel>
                <Select
                  value={pensionAgeBand}
                  label="Pension age band"
                  onChange={(e) => {
                    const b = e.target.value;
                    setPensionAgeBand(b);
                    api.put('/api/tax-preferences', { pensionAgeBand: b }).catch(() => {});
                  }}
                >
                  <MenuItem value="under30">Under 30 (15%)</MenuItem>
                  <MenuItem value="30-39">30–39 (20%)</MenuItem>
                  <MenuItem value="40-49">40–49 (25%)</MenuItem>
                  <MenuItem value="50-54">50–54 (30%)</MenuItem>
                  <MenuItem value="55-59">55–59 (35%)</MenuItem>
                  <MenuItem value="60+">60+ (40%)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Income sources (chart and table below)
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} alignItems="center">
                <TextField
                  size="small"
                  label="Search sources"
                  placeholder="e.g. Airbnb, RRLtd, 19Abb"
                  value={incomeSourceSearch}
                  onChange={(e) => setIncomeSourceSearch(e.target.value)}
                  sx={{ minWidth: 220, flex: '1 1 200px' }}
                />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={incomeTypeFilter}
                  onChange={(_, v) => v != null && setIncomeTypeFilter(v)}
                  aria-label="Income type filter"
                >
                  <ToggleButton value="all">All types</ToggleButton>
                  <ToggleButton value="rental">Rental only</ToggleButton>
                  <ToggleButton value="non-rental">Non-rental</ToggleButton>
                </ToggleButtonGroup>
                {(incomeSourceSearch.trim() !== '' || incomeTypeFilter !== 'all') && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setIncomeSourceSearch('');
                      setIncomeTypeFilter('all');
                    }}
                  >
                    Clear income filters
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Family Tabs at top */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={safeFamilyTab}
            onChange={(_, newValue) => setFamilyTab(newValue)}
            variant="fullWidth"
            sx={{ '& .MuiTab-root': { fontWeight: 600, fontSize: '1rem' } }}
          >
            {taxCalculations.map((calc, index) => (
              <Tab
                key={calc.familyId}
                label={getFamilyDisplayName(calc.familyName)}
                id={`family-tab-${index}`}
                aria-controls={`family-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab content - one panel per family */}
        {taxCalculations.map((calc, index) => (
          <div
            key={calc.familyId}
            role="tabpanel"
            hidden={safeFamilyTab !== index}
            id={`family-tabpanel-${index}`}
            aria-labelledby={`family-tab-${index}`}
          >
            {safeFamilyTab === index && (
              <Box sx={{ pt: 2 }}>
                {/* Scenario 1 / Scenario 2 tabs - only for Omar and Heidi */}
                {isOmHeFamily(calc.familyName || '') && (
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                      value={scenarioTab === 1 ? 2 : scenarioTab === 2 ? 1 : scenarioTab}
                      onChange={(_, tabIndex) => setScenarioTab(tabIndex === 1 ? 2 : tabIndex === 2 ? 1 : tabIndex)}
                      sx={{ minHeight: 40 }}
                    >
                      <Tab label="Comparison" id="scenario-tab-0" />
                      <Tab label="Scenario 1: No BTL" id="scenario-tab-2" />
                      <Tab label="Scenario 2: 19Abb" id="scenario-tab-1" />
                      <Tab label="Scenario 3: 19Abb + 51LC" id="scenario-tab-3" />
                    </Tabs>
                  </Box>
                )}
                {(scenarioLoading || (scenarioTab === 3 && !scenarioData3 && !scenarioData3Error)) && isOmHeFamily(calc.familyName || '') ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : scenarioTab === 3 && scenarioData3Error && isOmHeFamily(calc.familyName || '') ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                    <Typography color="text.secondary">Failed to load Scenario 3.</Typography>
                    <Button variant="contained" size="small" onClick={() => currentCalc && fetchBothScenarios(currentCalc.familyId)}>
                      Retry
                    </Button>
                  </Box>
                ) : scenarioTab === 0 && isOmHeFamily(calc.familyName || '') ? (
                  /* Comparison tab - each scenario uses its own pension (synced to max per scenario on load) */
                  (() => {
                    const pensions1 = (scenarioData1?.pensions ?? []).map(p => ({ heidi: p.heidi ?? 0, omar: p.omar ?? 0 }));
                    const pensions2 = (scenarioData2?.pensions ?? []).map(p => ({ heidi: p.heidi ?? 0, omar: p.omar ?? 0 }));
                    const pensions3 = (scenarioData3?.pensions ?? []).map(p => ({ heidi: p.heidi ?? 0, omar: p.omar ?? 0 }));
                    const s1 = computeScenarioSummary(scenarioData1?.incomes || [], scenarioData1?.loans || [], pensions1);
                    const s2 = computeScenarioSummary(scenarioData2?.incomes || [], scenarioData2?.loans || [], pensions2);
                    const s3 = computeScenarioSummary(scenarioData3?.incomes || [], scenarioData3?.loans || [], pensions3);
                    if (!s1 || !s2 || !s3) return <Typography color="text.secondary">Loading comparison...</Typography>;
                    // Expected: Tab1 (No BTL) < Tab2 (19Abb) < Tab3 (19Abb+51LC) for total income
                    const fmtPaPm = (v: number) => `${formatCurrency(v / 12)} p/m · ${formatCurrency(v)} p/a`;
                    const getMortgageLabel = (l: Loan) => (l.lender || l.loan_name || 'Mortgage').trim() || 'Mortgage';
                    const mortgageOrder = ['B&G', 'AIB', 'Pepper'];
                    const mortgageSortKey = (label: string) => {
                      const idx = mortgageOrder.findIndex(m => label.toLowerCase().includes(m.toLowerCase()));
                      return idx >= 0 ? idx : mortgageOrder.length;
                    };
                    const mortgageKeys = new Set<string>();
                    [s1.mortgageLoans, s2.mortgageLoans, s3.mortgageLoans].forEach(ml => (ml || []).forEach((l: Loan) => mortgageKeys.add(getMortgageLabel(l))));
                    const mortgageRows = Array.from(mortgageKeys).sort((a, b) => mortgageSortKey(a) - mortgageSortKey(b)).map(label => ({
                      label,
                      s1: -(s1.mortgageLoans?.find((l: Loan) => getMortgageLabel(l) === label)?.monthly_payment ?? 0) * 12,
                      s2: -(s2.mortgageLoans?.find((l: Loan) => getMortgageLabel(l) === label)?.monthly_payment ?? 0) * 12,
                      s3: -(s3.mortgageLoans?.find((l: Loan) => getMortgageLabel(l) === label)?.monthly_payment ?? 0) * 12,
                      color: 'error.main'
                    }));
                    // Gross to Net + Post-Net Expenses - same structure as each scenario
                    const rows: Array<{ label: string; s1: number; s2: number; s3: number; isPercent?: boolean; bold?: boolean; color?: string }> = [
                      { label: 'Gross earned income', s1: s1.earnedIncome, s2: s2.earnedIncome, s3: s3.earnedIncome },
                      { label: 'Gross rental income', s1: s1.rentalIncome, s2: s2.rentalIncome, s3: s3.rentalIncome },
                      { label: 'Total Gross income', s1: s1.totalIncome, s2: s2.totalIncome, s3: s3.totalIncome, bold: true },
                      { label: 'Personal Pension contribution', s1: s1.pensionAnnual, s2: s2.pensionAnnual, s3: s3.pensionAnnual },
                      { label: 'Taxable income', s1: s1.totalIncome - s1.pensionAnnual, s2: s2.totalIncome - s2.pensionAnnual, s3: s3.totalIncome - s3.pensionAnnual, bold: true },
                      { label: 'Income tax', s1: -s1.payeBeforeCredits, s2: -s2.payeBeforeCredits, s3: -s3.payeBeforeCredits, color: 'error.main' },
                      { label: 'USC', s1: -s1.uscTax, s2: -s2.uscTax, s3: -s3.uscTax, color: 'error.main' },
                      { label: 'PRSI', s1: -s1.prsiTax, s2: -s2.prsiTax, s3: -s3.prsiTax, color: 'error.main' },
                      { label: 'Tax credits', s1: s1.taxCredits, s2: s2.taxCredits, s3: s3.taxCredits, color: 'success.main' },
                      ...((s1.payeRelief ?? 0) > 0 || (s2.payeRelief ?? 0) > 0 || (s3.payeRelief ?? 0) > 0
                        ? [{ label: 'Pension relief', s1: s1.payeRelief ?? 0, s2: s2.payeRelief ?? 0, s3: s3.payeRelief ?? 0, color: 'success.main' }]
                        : []),
                      { label: 'Total Tax Paid', s1: -(s1.taxAfterPensionRelief ?? s1.totalTax), s2: -(s2.taxAfterPensionRelief ?? s2.totalTax), s3: -(s3.taxAfterPensionRelief ?? s3.totalTax), color: 'error.main', bold: true },
                      { label: 'Net income', s1: s1.netIncome, s2: s2.netIncome, s3: s3.netIncome, bold: true },
                      { label: 'Effective tax rate', s1: s1.totalIncome > 0 ? ((s1.taxAfterPensionRelief ?? s1.totalTax) / s1.totalIncome) * 100 : 0, s2: s2.totalIncome > 0 ? ((s2.taxAfterPensionRelief ?? s2.totalTax) / s2.totalIncome) * 100 : 0, s3: s3.totalIncome > 0 ? ((s3.taxAfterPensionRelief ?? s3.totalTax) / s3.totalIncome) * 100 : 0, isPercent: true },
                      ...mortgageRows,
                      { label: 'Total Mortgage Repayments', s1: -s1.mortgagesAnnual, s2: -s2.mortgagesAnnual, s3: -s3.mortgagesAnnual, color: 'error.main', bold: true },
                      { label: 'Disposable income', s1: s1.disposableIncome, s2: s2.disposableIncome, s3: s3.disposableIncome, bold: true }
                    ];
                    return (
                      <Card sx={{ mb: 4 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <ChartIcon />
                            <Chip label="Scenario 1: No BTL" size="small" sx={{ backgroundColor: '#e3f2fd' }} /> vs <Chip label="Scenario 2: 19Abb" size="small" sx={{ backgroundColor: '#e3f2fd' }} /> vs <Chip label="Scenario 3: 19Abb + 51LC" size="small" sx={{ backgroundColor: '#e3f2fd' }} />
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                            <Table size="small" sx={FIT_TABLE_SX}>
                              <FitColgroup
                                cols={equalFitCols(['line', 's1', 's2', 's3'], { flexKey: 'line', nonFlexTotal: 66 })}
                              />
                              <TableHead>
                                <TableRow>
                                  <TableCell>Line</TableCell>
                                  <TableCell align="right">Scenario 1: No BTL</TableCell>
                                  <TableCell align="right">Scenario 2: 19Abb</TableCell>
                                  <TableCell align="right">Scenario 3: 19Abb + 51LC</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.map((r) => {
                                  const fmt = r.isPercent ? (v: number) => `${v.toFixed(1)}%` : fmtPaPm;
                                  const boldSx = r.bold ? { fontWeight: 'bold' } : undefined;
                                  const colorSx = r.color ? { color: r.color } : undefined;
                                  const cellSx = { ...boldSx, ...colorSx };
                                  return (
                                    <TableRow key={r.label} sx={boldSx}>
                                      <TableCell sx={cellSx}>
                                        <Box component="span" sx={fitTruncateSx} title={r.label}>{r.label}</Box>
                                      </TableCell>
                                      <TableCell align="right" sx={cellSx}>{fmt(r.s2)}</TableCell>
                                      <TableCell align="right" sx={cellSx}>{fmt(r.s1)}</TableCell>
                                      <TableCell align="right" sx={cellSx}>{fmt(r.s3)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Scenario Comparison: Income, Pension, Tax, Mortgages and Disposable Income</Typography>
                            <ResponsiveContainer width="100%" height={420}>
                              <BarChart
                                data={[
                                  { name: 'Total Income', s1: s1.totalIncome, s2: s2.totalIncome, s3: s3.totalIncome },
                                  { name: 'Pension Contribution', s1: s1.pensionAnnual, s2: s2.pensionAnnual, s3: s3.pensionAnnual },
                                  { name: 'Tax Paid', s1: s1.taxAfterPensionRelief ?? s1.totalTax, s2: s2.taxAfterPensionRelief ?? s2.totalTax, s3: s3.taxAfterPensionRelief ?? s3.totalTax },
                                  { name: 'Mortgage Repayments', s1: s1.mortgagesAnnual, s2: s2.mortgagesAnnual, s3: s3.mortgagesAnnual },
                                  { name: 'Disposable Income', s1: s1.disposableIncome, s2: s2.disposableIncome, s3: s3.disposableIncome }
                                ]}
                                layout="vertical"
                                margin={{ top: 5, right: chartRightPad, left: chartLeftPad, bottom: 5 }}
                                barCategoryGap="20%"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                                <YAxis type="category" dataKey="name" width={chartLeftPad} tick={{ fontSize: 14 }} />
                                <RechartsTooltip formatter={(v: number, name: string) => [`${formatCurrency(v / 12)} p/m · ${formatCurrency(v)} p/a`, name]} labelFormatter={(l) => l} />
                                <Legend />
                                {(() => {
                                  const fmt = (v: number) => `${formatCurrency(v / 12)} p/m · ${formatCurrency(v)} p/a`;
                                  return (
                                    <>
                                <Bar dataKey="s2" fill={S2_HEIDI_COLOR} name="Scenario 1: No BTL" barSize={36}>
                                  <LabelList dataKey="s2" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                <Bar dataKey="s1" fill={S1_HEIDI_COLOR} name="Scenario 2: 19Abb" barSize={36}>
                                  <LabelList dataKey="s1" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                <Bar dataKey="s3" fill={S3_HEIDI_COLOR} name="Scenario 3: 19Abb + 51LC" barSize={36}>
                                  <LabelList dataKey="s3" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                    </>
                                  );
                                })()}
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Income Sources – Scenario 1: No BTL vs Scenario 2: 19Abb vs Scenario 3: 19Abb + 51LC</Typography>
                            <Box sx={{ overflow: 'visible', width: '100%' }}>
                            <ResponsiveContainer width="100%" height={480}>
                              <BarChart
                                data={(() => {
                                  const api1 = buildSourceRowsFromIncomes(scenarioData1?.incomes || []);
                                  const api2 = buildSourceRowsFromIncomes(scenarioData2?.incomes || []);
                                  const api3 = buildSourceRowsFromIncomes(scenarioData3?.incomes || []);
                                  const byKey = new Map<string, { name: string; s1Heidi: number; s1Omar: number; s2Heidi: number; s2Omar: number; s3Heidi: number; s3Omar: number }>();
                                  const rowOrder = ['lucena', 'rotunda', 'enable', 'apa', 'abberley', '19abb', '51lc', '87hp', 'from rrltd', 'from omheprops', 'omhe'];
                                  const mergeIn = (src: ReturnType<typeof buildSourceRowsFromIncomes>, sKey: 's1' | 's2' | 's3') => {
                                    src.forEach(r => {
                                      const name = getDetailsDisplayForChart(r.details);
                                      const existing = byKey.get(r.key);
                                      if (existing) {
                                        (existing as any)[`${sKey}Heidi`] = r.heidi;
                                        (existing as any)[`${sKey}Omar`] = r.omar;
                                      } else {
                                        byKey.set(r.key, {
                                          name, s1Heidi: 0, s1Omar: 0, s2Heidi: 0, s2Omar: 0, s3Heidi: 0, s3Omar: 0,
                                          ...{ [`${sKey}Heidi`]: r.heidi, [`${sKey}Omar`]: r.omar }
                                        });
                                      }
                                    });
                                  };
                                  mergeIn(api2, 's1');
                                  mergeIn(api1, 's2');
                                  mergeIn(api3, 's3');
                                  return Array.from(byKey.entries())
                                    .map(([, v]) => v)
                                    .sort((a, b) => {
                                      const ai = rowOrder.findIndex(o => a.name.toLowerCase().includes(o));
                                      const bi = rowOrder.findIndex(o => b.name.toLowerCase().includes(o));
                                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                                    });
                                })()}
                                layout="vertical"
                                margin={{ top: 5, right: chartRightPad, left: incomeSourceChartLeftPad, bottom: 5 }}
                                barCategoryGap="28%"
                                barGap={8}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                                <YAxis type="category" dataKey="name" width={incomeSourceYAxisWidth} tick={<IncomeSourceYAxisTick />} />
                                <RechartsTooltip formatter={(v: number, name: string) => [`${formatCurrency(v / 12)} p/m · ${formatCurrency(v)} p/a`, name]} labelFormatter={(l) => l} />
                                <Legend />
                                {(() => {
                                  const fmt = (v: number) => `${formatCurrency(v / 12)} p/m · ${formatCurrency(v)} p/a`;
                                  return (
                                    <>
                                <Bar dataKey="s1Heidi" fill={S1_HEIDI_COLOR} stackId="s1" name="S1: No BTL Heidi" barSize={44} minPointSize={1}>
                                  <LabelList dataKey="s1Heidi" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                <Bar dataKey="s1Omar" fill={S1_OMAR_COLOR} stackId="s1" name="S1: No BTL Omar" barSize={44} minPointSize={1}>
                                  <LabelList dataKey="s1Omar" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                <Bar dataKey="s2Heidi" fill={S2_HEIDI_COLOR} stackId="s2" name="S2: 19Abb Heidi" barSize={44} minPointSize={1}>
                                  <LabelList dataKey="s2Heidi" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                <Bar dataKey="s2Omar" fill={S2_OMAR_COLOR} stackId="s2" name="S2: 19Abb Omar" barSize={44} minPointSize={1}>
                                  <LabelList dataKey="s2Omar" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                <Bar dataKey="s3Heidi" fill={S3_HEIDI_COLOR} stackId="s3" name="S3: 19Abb+51LC Heidi" barSize={44} minPointSize={1}>
                                  <LabelList dataKey="s3Heidi" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                <Bar dataKey="s3Omar" fill={S3_OMAR_COLOR} stackId="s3" name="S3: 19Abb+51LC Omar" barSize={44} minPointSize={1}>
                                  <LabelList dataKey="s3Omar" content={(p: any) => <BarLabelContent {...p} formatter={fmt} />} />
                                </Bar>
                                    </>
                                  );
                                })()}
                              </BarChart>
                            </ResponsiveContainer>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })()
                ) : (
                (() => {
                  const isOmHe = isOmHeFamily(calc.familyName || '');
                  const effectivePensions = (isOmHe && effectiveScenarioData) ? effectiveScenarioData.pensions : (scenarioDataByFamily[calc.familyId]?.pensions ?? []);
                  const effectiveIncomes = isOmHe && effectiveScenarioData ? effectiveScenarioData.incomes : incomes;
                  const effectiveLoans = isOmHe && effectiveScenarioData ? effectiveScenarioData.loans : loans;
                  return (
                <>
                {/* Scenario description - static text with edit icon top right (S3 is read-only, no edit) */}
                {isOmHe && effectiveScenarioData && (
                  <Box sx={{ mb: 2, position: 'relative', pr: effectiveScenarioData.scenarioId ? 5 : 0 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minHeight: 40, py: 1.5 }}
                    >
                      {effectiveScenarioData.description?.trim() || 'Add a description for this scenario...'}
                    </Typography>
                    {effectiveScenarioData.scenarioId && (
                      <Tooltip title="Edit description">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setScenarioDescEditValue(effectiveScenarioData!.description ?? '');
                            setScenarioDescEditOpen(true);
                          }}
                          aria-label="Edit scenario description"
                          sx={{ position: 'absolute', top: 0, right: 0 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
                {/* Tax Summary for this family */}
                <Card sx={{ mb: 4 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChartIcon />
                      Annual Income Pension and Tax Summary
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                      <Table size="small" sx={FIT_TABLE_SX}>
                        <FitColgroup
                          cols={equalFitCols(
                            [
                              'family',
                              'tax_status',
                              'total_income',
                              'pension',
                              'income_tax',
                              'tax_credits',
                              'usc',
                              'prsi',
                              'total_tax',
                              'net_income',
                              'effective_rate',
                            ],
                            { flexKey: 'family', nonFlexTotal: 86 }
                          )}
                        />
                        <TableHead>
                          <TableRow>
                            <TableCell>Family</TableCell>
                            <TableCell>Tax Status</TableCell>
                            <TableCell align="right">Total Income</TableCell>
                            <TableCell align="right">Pension</TableCell>
                            <TableCell align="right">Income Tax</TableCell>
                            <TableCell align="right">Tax Credits</TableCell>
                            <TableCell align="right">USC</TableCell>
                            <TableCell align="right">PRSI</TableCell>
                            <TableCell align="right">Total Tax</TableCell>
                            <TableCell align="right">Net Income</TableCell>
                            <TableCell align="right">Effective Tax Rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <FamilyIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  sx={fitTruncateSx}
                                  title={getFamilyDisplayName(calc.familyName)}
                                >
                                  {getFamilyDisplayName(calc.familyName)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getFamilyType(calc.familyName)}
                                size="small"
                                color={calc.familyName === 'SeCo' ? 'secondary' : 'primary'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                +{formatCurrency(calc.totalIncome)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {(() => {
                                  const totalPension = effectivePensions.reduce((s, r) => s + r.heidi + r.omar, 0);
                                  return totalPension > 0 ? `-${formatCurrency(totalPension)}` : formatCurrency(0);
                                })()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="error">
                                -{formatCurrency(calc.payeTax)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="success.main">
                                +{formatCurrency((calc.breakdown.taxCreditsBreakdown ?? []).reduce((s, c) => s + c.amount, 0))}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="error">
                                -{formatCurrency(calc.uscTax)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="error">
                                -{formatCurrency(calc.prsiTax)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="error">
                                -{formatCurrency(calc.totalTax)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="success.main">
                                +{formatCurrency((() => {
                                  const totalPension = effectivePensions.reduce((s, r) => s + r.heidi + r.omar, 0);
                                  if (totalPension <= 0) return calc.netIncome;
                                  if (calc === currentCalc && pensionReliefResult) {
                                    return calc.netIncome - totalPension + pensionReliefResult.payeRelief;
                                  }
                                  return calc.netIncome;
                                })())}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`-${calc.taxRate.toFixed(1)}%`}
                                size="small"
                                color={calc.taxRate > 30 ? 'error' : calc.taxRate > 20 ? 'warning' : 'success'}
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Income Sources - uses backend summaries when available */}
                {(() => {
                  const getSourceLabel = (name: string) => {
                    if (!name) return '';
                    if (name.toLowerCase().includes('lucena') || name.toLowerCase().includes('rotunda')) return 'Lucena';
                    if (name.toLowerCase().includes('enable')) return 'Enable Ireland';
                    if (name.toLowerCase().includes('apa')) return 'APA';
                    if (name.toLowerCase().includes('abberley') || name.toLowerCase().includes('19abb')) return 'B2L Property';
                    if (name.toLowerCase().includes('87hp')) return 'RRLtd';
                    if (name.toLowerCase().includes('rrltd')) return 'RRLtd';
                    if (name.toLowerCase().includes('omhe') || name.toLowerCase().includes('omheprops')) return 'OmHeProps';
                    return name;
                  };
                  const getInternalExternalDisplay = (details: string, source: string) => {
                    const name = (details || '').toLowerCase();
                    if (name.includes('87hp')) return 'Internal';
                    return source;
                  };
                  const getDetailsDisplay = (name: string) => {
                    if (!name) return '';
                    const lower = name.toLowerCase();
                    if (lower.includes('abberley')) return '19Abb rental income';
                    if (lower.includes('from rrltd')) return 'PAYE Salary from RRLtd';
                    if (lower.includes('from omheprops') || lower.includes('omheprops')) return 'PAYE Salary from OmHeProps';
                    if (lower.includes('enable') && lower.includes('assesment')) return 'Enable Ireland Laptop Assessment';
                    return name;
                  };
                  // Prefer backend summaries: scenario data (OmHe) or per-family summary
                  const backendSummary =
                    (isOmHe && effectiveScenarioData?.incomeSummary && calc.familyId === currentCalc?.familyId)
                      ? effectiveScenarioData.incomeSummary
                      : incomeSummaryByFamily[calc.familyId];
                  let sourceRows: Array<{ key: string; details: string; heidi: number; omar: number; type: string; source?: string; records: any[] }>;
                  let totalHeidi: number;
                  let totalOmar: number;
                  let totalCombined: number;
                  let totalRental: number;
                  let totalNonRental: number;
                  let totalRrltd: number;
                  if (backendSummary?.sourceRows?.length != null) {
                    // In scenario mode: merge all sources from s1/s2/s3, use current scenario values or 0 for missing
                    const rowOrder = ['lucena', 'rotunda', 'enable', 'apa', 'abberley', '19abb', '51lc', '87hp', 'from rrltd', 'from omheprops', 'omhe'];
                    if (isOmHe && scenarioData1?.incomeSummary && scenarioData2?.incomeSummary && scenarioData3?.incomeSummary) {
                      const allRows = [
                        ...(scenarioData1.incomeSummary.sourceRows || []),
                        ...(scenarioData2.incomeSummary.sourceRows || []),
                        ...(scenarioData3.incomeSummary.sourceRows || [])
                      ];
                      const byKey = new Map<string, { key: string; details: string; heidi: number; omar: number; type: string; records: any[] }>();
                      allRows.forEach((r: any) => {
                        const k = r.key || r.details || '';
                        if (!k) return;
                        if (!byKey.has(k)) byKey.set(k, { key: k, details: r.details || k, heidi: 0, omar: 0, type: r.type || 'Other', records: [] });
                      });
                      (backendSummary.sourceRows || []).forEach((r: any) => {
                        const k = r.key || r.details || '';
                        const existing = byKey.get(k);
                        if (existing) {
                          existing.heidi = r.heidi ?? 0;
                          existing.omar = r.omar ?? 0;
                          existing.type = r.type || existing.type;
                          existing.records = r.records || [];
                        }
                      });
                      sourceRows = Array.from(byKey.values()).sort((a, b) => {
                        const ai = rowOrder.findIndex(o => (a.details || '').toLowerCase().includes(o));
                        const bi = rowOrder.findIndex(o => (b.details || '').toLowerCase().includes(o));
                        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                      });
                    } else {
                      sourceRows = backendSummary.sourceRows;
                    }
                    totalHeidi = backendSummary.totalHeidi ?? 0;
                    totalOmar = backendSummary.totalOmar ?? 0;
                    totalCombined = backendSummary.totalCombined ?? totalHeidi + totalOmar;
                    totalRental = backendSummary.totalRental ?? 0;
                    totalNonRental = backendSummary.totalNonRental ?? totalCombined - totalRental;
                    totalRrltd = backendSummary.totalRrltd ?? 0;
                  } else {
                    const getPersonFamilyId = (p: Person | undefined) => p && ((p as any).family_id ?? (p as any).familyId);
                    const familyIncomes = isOmHe && effectiveScenarioData ? effectiveIncomes : incomes.filter(inc => getPersonFamilyId(inc.person) === calc.familyId);
                    const bySource = new Map<string, { heidi: number; omar: number; income_type: string; source: string; details: string; records: any[] }>();
                    familyIncomes.forEach((income: any) => {
                      if (!income.person) return;
                      const name = income.income_name || 'Unknown';
                      if (!bySource.has(name)) {
                        bySource.set(name, { heidi: 0, omar: 0, income_type: income.income_type || 'Other', source: income.source || 'External', details: name, records: [] });
                      }
                      const row = bySource.get(name)!;
                      row.records.push(income);
                      const amt = income.amount_yearly ?? 0;
                      if (income.person.name?.toLowerCase().includes('heidi')) row.heidi += amt;
                      else if (income.person.name?.toLowerCase().includes('omar')) row.omar += amt;
                    });
                    const rowOrder = ['lucena', 'rotunda', 'enable', 'apa', 'abberley', '19abb', '51lc', '87hp', 'from rrltd', 'from omheprops', 'omhe'];
                    sourceRows = Array.from(bySource.entries())
                      .map(([k, v]) => ({ key: k, ...v, type: getTypeLabelFromIncome(v.records[0] as Income) }))
                      .sort((a, b) => {
                        const ai = rowOrder.findIndex(o => a.details.toLowerCase().includes(o));
                        const bi = rowOrder.findIndex(o => b.details.toLowerCase().includes(o));
                        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                      });
                    totalHeidi = sourceRows.reduce((s, r) => s + r.heidi, 0);
                    totalOmar = sourceRows.reduce((s, r) => s + r.omar, 0);
                    totalCombined = totalHeidi + totalOmar;
                    const rentalRows = sourceRows.filter(r => r.type === 'Rental Income');
                    totalRental = rentalRows.reduce((s, r) => s + r.heidi + r.omar, 0);
                    totalRrltd = sourceRows.filter(r => getSourceLabel(r.details) === 'RRLtd').reduce((s, r) => s + r.heidi + r.omar, 0);
                    totalNonRental = totalCombined - totalRental;
                  }
                  const rentalRows = sourceRows.filter(r => r.type === 'Rental Income');
                  const displaySourceRows = filterIncomeSourceRowsForDisplay(
                    sourceRows,
                    incomeSourceSearch,
                    incomeTypeFilter
                  );
                  const incomeFiltersActive =
                    incomeSourceSearch.trim() !== '' || incomeTypeFilter !== 'all';
                  const displayRentalRows = displaySourceRows.filter(
                    (r) => r.type === 'Rental Income' || isRentalIncomeType({ income_name: r.details })
                  );
                  const footHeidi = incomeFiltersActive
                    ? displaySourceRows.reduce((s, r) => s + r.heidi, 0)
                    : totalHeidi;
                  const footOmar = incomeFiltersActive
                    ? displaySourceRows.reduce((s, r) => s + r.omar, 0)
                    : totalOmar;
                  const footCombined = footHeidi + footOmar;
                  const footRental = displayRentalRows.reduce((s, r) => s + r.heidi + r.omar, 0);
                  const footNonRental = footCombined - footRental;
                  const footRrltdHeidi = displaySourceRows
                    .filter((r) => getSourceLabel(r.details) === 'RRLtd')
                    .reduce((s, r) => s + r.heidi, 0);
                  const footRrltdOmar = displaySourceRows
                    .filter((r) => getSourceLabel(r.details) === 'RRLtd')
                    .reduce((s, r) => s + r.omar, 0);
                  const footRrltd = footRrltdHeidi + footRrltdOmar;
                  const rentHeidiFoot = incomeFiltersActive
                    ? displayRentalRows.reduce((s, r) => s + r.heidi, 0)
                    : rentalRows.reduce((s, r) => s + r.heidi, 0);
                  const rentOmarFoot = incomeFiltersActive
                    ? displayRentalRows.reduce((s, r) => s + r.omar, 0)
                    : rentalRows.reduce((s, r) => s + r.omar, 0);
                  const rentCombinedFoot = incomeFiltersActive ? footRental : totalRental;
                  const nonRentHeidiFoot = incomeFiltersActive
                    ? footHeidi - displayRentalRows.reduce((s, r) => s + r.heidi, 0)
                    : totalHeidi - rentalRows.reduce((s, r) => s + r.heidi, 0);
                  const nonRentOmarFoot = incomeFiltersActive
                    ? footOmar - displayRentalRows.reduce((s, r) => s + r.omar, 0)
                    : totalOmar - rentalRows.reduce((s, r) => s + r.omar, 0);
                  const nonRentCombinedFoot = incomeFiltersActive ? footNonRental : totalNonRental;
                  return (
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <EuroIcon />
                            Income Sources
                            {incomeFiltersActive && (
                              <Chip size="small" color="info" label="Filters on" variant="outlined" />
                            )}
                          </Typography>
                          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setIncomeAddFamilyId(calc.familyId); setIncomeAddForm({ heidiAmount: 0, omarAmount: 0, type: 'PAYE', sourceEmployer: 'Lucena', internalExternal: 'External', details: '' }); setIncomeAddOpen(true); }}>
                            Add new row
                          </Button>
                        </Box>
                        {displaySourceRows.length > 0 && isOmHe && (
                          <Box sx={{ mb: 3, height: 400, overflow: 'visible' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={displaySourceRows.map(r => ({
                                  name: getDetailsDisplayForChart(r.details),
                                  Heidi: r.heidi,
                                  Omar: r.omar
                                }))}
                                layout="vertical"
                                margin={{ top: 5, right: chartRightPad, left: incomeSourceChartLeftPad, bottom: 5 }}
                                barCategoryGap="20%"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                                <YAxis type="category" dataKey="name" width={incomeSourceYAxisWidth} tick={<IncomeSourceYAxisTick />} />
                                <RechartsTooltip formatter={(v: number, name: string) => [`€${v.toLocaleString()}`, name]} labelFormatter={(l) => l} />
                                <Legend />
                                <Bar dataKey="Heidi" fill={scenarioTab === 2 ? S2_HEIDI_COLOR : scenarioTab === 3 ? S3_HEIDI_COLOR : S1_HEIDI_COLOR} stackId="a" name="Heidi" minPointSize={1}>
                                  <LabelList dataKey="Heidi" content={(p: any) => <BarLabelContent {...p} formatter={(v) => `€${v.toLocaleString()}`} />} />
                                </Bar>
                                <Bar dataKey="Omar" fill={OMAR_BURNT_ORANGE} stackId="a" name="Omar" minPointSize={1}>
                                  <LabelList dataKey="Omar" content={(p: any) => <BarLabelContent {...p} formatter={(v) => `€${v.toLocaleString()}`} />} />
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                        {sourceRows.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <EuroIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                              No income records for this family. Add income to see tax calculations.
                            </Typography>
                          </Box>
                        ) : displaySourceRows.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              No income sources match the current filters.
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setIncomeSourceSearch('');
                                setIncomeTypeFilter('all');
                              }}
                            >
                              Clear income filters
                            </Button>
                          </Box>
                        ) : (
                          <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                            <Table size="small" sx={FIT_TABLE_SX}>
                              <FitColgroup
                                cols={[
                                  { key: 'source', pct: 11 },
                                  { key: 'type', pct: 10 },
                                  { key: 'internal_external', pct: 10 },
                                  { key: 'heidi', pct: 11 },
                                  { key: 'omar', pct: 11 },
                                  { key: 'combined_pa', pct: 11 },
                                  { key: 'combined_pm', pct: 11 },
                                  { key: 'details' },
                                  { key: 'actions', pct: 9 },
                                ]}
                              />
                              <TableHead>
                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Source</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Internal{'\n'}External</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Heidi{'\n'}per annum</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Omar{'\n'}per annum</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Combined{'\n'}per annum</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Combined{'\n'}per month</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Details</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {displaySourceRows.map((row) => {
                                  const combined = row.heidi + row.omar;
                                  const monthly = combined / 12;
                                  const firstRecord = row.records[0];
                                  return (
                                    <TableRow key={row.key} hover>
                                      <TableCell>
                                        <Chip label={getSourceLabel(row.details)} size="small" variant="outlined" sx={fitChipSx} />
                                      </TableCell>
                                      <TableCell>
                                        <Chip label={row.type || getTypeLabelFromIncome(firstRecord)} size="small" variant="outlined" sx={fitChipSx} />
                                      </TableCell>
                                      <TableCell>
                                        <Chip label={getInternalExternalDisplay(row.details, row.source ?? row.records?.[0]?.source ?? 'External')} size="small" variant="outlined" sx={fitChipSx} color={getInternalExternalDisplay(row.details, row.source ?? row.records?.[0]?.source ?? 'External') === 'Internal' ? 'primary' : 'default'} />
                                      </TableCell>
                                      <TableCell>{formatCurrency(row.heidi)}</TableCell>
                                      <TableCell align="right">{formatCurrency(row.omar)}</TableCell>
                                      <TableCell align="right">{formatCurrency(combined)}</TableCell>
                                      <TableCell align="right">{formatCurrency(monthly)}</TableCell>
                                      <TableCell>
                                        <Box component="span" sx={fitTruncateSx} title={row.details || ''}>
                                          {getDetailsDisplay(row.details)}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center" className="fit-table-no-clip">
                                        <Tooltip title="Edit">
                                          <IconButton size="small" onClick={() => handleIncomeSourceEdit(row.records)}>
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton size="small" color="error" onClick={() => { setIncomeToDeleteRecords(row.records); setIncomeSourceDeleteOpen(true); }}>
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                                  <TableCell colSpan={3} />
                                  <TableCell>{formatCurrency(footHeidi)}</TableCell>
                                  <TableCell align="right">{formatCurrency(footOmar)}</TableCell>
                                  <TableCell align="right">{formatCurrency(footCombined)}</TableCell>
                                  <TableCell align="right">{formatCurrency(footCombined / 12)}</TableCell>
                                  <TableCell colSpan={2}>
                                    TOTAL GROSS INCOME
                                    {incomeFiltersActive ? ' (filtered)' : ''}
                                  </TableCell>
                                </TableRow>
                                <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                                  <TableCell colSpan={3} />
                                  <TableCell>{formatCurrency(footRrltdHeidi)}</TableCell>
                                  <TableCell align="right">{formatCurrency(footRrltdOmar)}</TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(incomeFiltersActive ? footRrltd : totalRrltd)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency((incomeFiltersActive ? footRrltd : totalRrltd) / 12)}
                                  </TableCell>
                                  <TableCell colSpan={2}>Total from RRLtd</TableCell>
                                </TableRow>
                                <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                                  <TableCell colSpan={3} />
                                  <TableCell>{formatCurrency(rentHeidiFoot)}</TableCell>
                                  <TableCell align="right">{formatCurrency(rentOmarFoot)}</TableCell>
                                  <TableCell align="right">{formatCurrency(rentCombinedFoot)}</TableCell>
                                  <TableCell align="right">{formatCurrency(rentCombinedFoot / 12)}</TableCell>
                                  <TableCell colSpan={2}>Total Rental Income</TableCell>
                                </TableRow>
                                <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                                  <TableCell colSpan={3} />
                                  <TableCell>{formatCurrency(nonRentHeidiFoot)}</TableCell>
                                  <TableCell align="right">{formatCurrency(nonRentOmarFoot)}</TableCell>
                                  <TableCell align="right">{formatCurrency(nonRentCombinedFoot)}</TableCell>
                                  <TableCell align="right">{formatCurrency(nonRentCombinedFoot / 12)}</TableCell>
                                  <TableCell colSpan={2}>Total Non-rental income</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Personal Pension Contributions */}
                <Card sx={{ mb: 4 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SavingsIcon />
                        Personal Pension Contributions
                      </Typography>
                      <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => {
                        setPensionAddForm({ name: '', heidi: 0, omar: 0, totalContributions: 0, currentValue: 0 });
                        setPensionAddOpen(true);
                      }}>
                        Add new row
                      </Button>
                    </Box>
                    {effectivePensions.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <SavingsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No pension entries. Add a pension to track contributions and growth.
                        </Typography>
                      </Box>
                    ) : (
                    <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                      <Table size="small" sx={FIT_TABLE_SX}>
                        <FitColgroup
                          cols={[
                            { key: 'pension' },
                            { key: 'heidi', pct: 14 },
                            { key: 'omar', pct: 14 },
                            { key: 'total_contributions', pct: 15 },
                            { key: 'current_value', pct: 15 },
                            { key: 'growth', pct: 12 },
                            { key: 'actions', pct: 10 },
                          ]}
                        />
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pension</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Heidi{'\n'}this year</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Omar{'\n'}this year</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Total{'\n'}contributions</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Current Value</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>% Growth</TableCell>
                            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {effectivePensions.map((row, idx) => {
                            const growth = row.totalContributions > 0 ? ((row.currentValue - row.totalContributions) / row.totalContributions) * 100 : 0;
                            return (
                              <TableRow key={`${row.name}-${idx}`} hover>
                                <TableCell>
                                  <Box component="span" sx={fitTruncateSx} title={row.name}>{row.name}</Box>
                                </TableCell>
                                <TableCell align="right">{formatCurrency(row.heidi)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.omar)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.totalContributions)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.currentValue)}</TableCell>
                                <TableCell align="right">
                                  <Chip label={`${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`} size="small" color={growth >= 0 ? 'success' : 'error'} variant="outlined" />
                                </TableCell>
                                <TableCell align="center" className="fit-table-no-clip">
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => {
                                      setPensionEditIdx(idx);
                                      setPensionEditId((row as any).id ?? null);
                                      setPensionEditForm({ ...row });
                                      setPensionEditOpen(true);
                                    }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => {
                                      setPensionDeleteIdx(idx);
                                      setPensionDeleteId((row as any).id ?? null);
                                      setPensionDeleteOpen(true);
                                    }}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                            <TableCell>TOTAL</TableCell>
                            <TableCell align="right">{formatCurrency(effectivePensions.reduce((s, r) => s + r.heidi, 0))}</TableCell>
                            <TableCell align="right">{formatCurrency(effectivePensions.reduce((s, r) => s + r.omar, 0))}</TableCell>
                            <TableCell align="right">{formatCurrency(effectivePensions.reduce((s, r) => s + r.totalContributions, 0))}</TableCell>
                            <TableCell align="right">{formatCurrency(effectivePensions.reduce((s, r) => s + r.currentValue, 0))}</TableCell>
                            <TableCell align="right">
                              {(() => {
                                const totalContrib = effectivePensions.reduce((s, r) => s + r.totalContributions, 0);
                                const totalValue = effectivePensions.reduce((s, r) => s + r.currentValue, 0);
                                const totalGrowth = totalContrib > 0 ? ((totalValue - totalContrib) / totalContrib) * 100 : 0;
                                return <Chip label={`${totalGrowth >= 0 ? '+' : ''}${totalGrowth.toFixed(1)}%`} size="small" color={totalGrowth >= 0 ? 'success' : 'error'} />;
                              })()}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    )}
                  </CardContent>
                </Card>

                {/* Tax Breakdown for this family */}
                <Card sx={{ mb: 4 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FamilyIcon color="primary" />
                        <Typography variant="h6">Tax Breakdown</Typography>
                        <Chip
                          label={getFamilyType(calc.familyName)}
                          size="small"
                          color={calc.familyName === 'SeCo' ? 'secondary' : 'primary'}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">PAYE</Typography>
                          <Typography variant="body1" color="error" fontWeight="medium">{formatCurrency(calc.payeTax)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">USC</Typography>
                          <Typography variant="body1" color="error" fontWeight="medium">{formatCurrency(calc.uscTax)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">PRSI</Typography>
                          <Typography variant="body1" color="error" fontWeight="medium">{formatCurrency(calc.prsiTax)}</Typography>
                        </Box>
                      </Box>
                    </Box>

                <Box sx={{ display: 'flex', gap: 3 }}>
                  {/* PAYE Breakdown */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EuroIcon fontSize="small" />
                      Income Tax
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                      <Table size="small" sx={FIT_TABLE_SX}>
                        <FitColgroup
                          cols={[
                            ...(calc.breakdown.payeTwoEarnerRows ? [{ key: 'person', pct: 16 }] : []),
                            { key: 'band' },
                            { key: 'rate', pct: 14 },
                            { key: 'amount', pct: 20 },
                            { key: 'tax', pct: 20 },
                          ]}
                        />
                        <TableHead>
                          <TableRow>
                            {calc.breakdown.payeTwoEarnerRows && <TableCell>Person</TableCell>}
                            <TableCell>Band</TableCell>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Tax</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {calc.breakdown.payeTwoEarnerRows ? (
                            <>
                              {calc.breakdown.payeTwoEarnerRows.map((row, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Box component="span" sx={fitTruncateSx} title={row.person}>{row.person}</Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box component="span" sx={fitTruncateSx} title={row.band}>{row.band}</Box>
                                  </TableCell>
                                  <TableCell align="right">{row.rate.toFixed(1)}%</TableCell>
                                  <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                                  <TableCell align="right">{formatCurrency(row.tax)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 'bold' } }}>
                                <TableCell colSpan={2}>Pre-Credits Total</TableCell>
                                <TableCell align="right">-</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(calc.breakdown.payeTwoEarnerRows.reduce((s, r) => s + r.amount, 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(calc.breakdown.payeBeforeCredits ?? calc.breakdown.payeTwoEarnerRows.reduce((s, r) => s + r.tax, 0))}
                                </TableCell>
                              </TableRow>
                              {(calc.breakdown.taxCreditsBreakdown ?? []).map((credit, idx) => (
                                <TableRow key={idx}>
                                  <TableCell colSpan={2}>
                                    <Box component="span" sx={fitTruncateSx} title={credit.label}>{credit.label}</Box>
                                  </TableCell>
                                  <TableCell align="right">-</TableCell>
                                  <TableCell align="right">{formatCurrency(credit.amount)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>-{formatCurrency(credit.amount)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 'bold' }, borderTop: 2, borderColor: 'divider' }}>
                                <TableCell colSpan={2}>Income Tax Total</TableCell>
                                <TableCell align="right">-</TableCell>
                                <TableCell align="right">-</TableCell>
                                <TableCell align="right">{formatCurrency(calc.payeTax)}</TableCell>
                              </TableRow>
                            </>
                          ) : (
                            <>
                              {calc.breakdown.payeBands.map((band, bandIndex) => (
                                <TableRow key={bandIndex}>
                                  <TableCell>
                                    <Box component="span" sx={fitTruncateSx} title={band.band}>{band.band}</Box>
                                  </TableCell>
                                  <TableCell align="right">{band.rate.toFixed(1)}%</TableCell>
                                  <TableCell align="right">{formatCurrency(band.amount)}</TableCell>
                                  <TableCell align="right">{formatCurrency(band.tax)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 'bold' } }}>
                                <TableCell colSpan={2}>Pre-Credits Total</TableCell>
                                <TableCell align="right">-</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(calc.breakdown.payeBands.reduce((sum, band) => sum + band.amount, 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(calc.breakdown.payeBeforeCredits ?? calc.breakdown.payeBands.reduce((sum, band) => sum + band.tax, 0))}
                                </TableCell>
                              </TableRow>
                              {(calc.breakdown.taxCreditsBreakdown ?? []).map((credit, idx) => (
                                <TableRow key={idx}>
                                  <TableCell colSpan={2}>
                                    <Box component="span" sx={fitTruncateSx} title={credit.label}>{credit.label}</Box>
                                  </TableCell>
                                  <TableCell align="right">-</TableCell>
                                  <TableCell align="right">{formatCurrency(credit.amount)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>-{formatCurrency(credit.amount)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 'bold' }, borderTop: 2, borderColor: 'divider' }}>
                                <TableCell colSpan={2}>Income Tax Total</TableCell>
                                <TableCell align="right">-</TableCell>
                                <TableCell align="right">-</TableCell>
                                <TableCell align="right">{formatCurrency(calc.payeTax)}</TableCell>
                              </TableRow>
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* USC Breakdown */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EuroIcon fontSize="small" />
                      USC
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                      <Table size="small" sx={FIT_TABLE_SX}>
                        <FitColgroup
                          cols={[
                            { key: 'band' },
                            { key: 'rate', pct: 14 },
                            { key: 'amount', pct: 22 },
                            { key: 'tax', pct: 22 },
                          ]}
                        />
                        <TableHead>
                          <TableRow>
                            <TableCell>Band</TableCell>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Tax</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {calc.breakdown.uscBands.map((band, bandIndex) => (
                            <TableRow key={bandIndex}>
                              <TableCell>
                                <Box component="span" sx={fitTruncateSx} title={band.band}>{band.band}</Box>
                              </TableCell>
                              <TableCell align="right">{band.rate.toFixed(1)}%</TableCell>
                              <TableCell align="right">{formatCurrency(band.amount)}</TableCell>
                              <TableCell align="right">{formatCurrency(band.tax)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 'bold' } }}>
                            <TableCell>Total</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">
                              {formatCurrency(calc.breakdown.uscBands.reduce((sum, band) => sum + band.amount, 0))}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(calc.breakdown.uscBands.reduce((sum, band) => sum + band.tax, 0))}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* PRSI Breakdown */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EuroIcon fontSize="small" />
                      PRSI (Pay Related Social Insurance)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                      <Table size="small" sx={FIT_TABLE_SX}>
                        <FitColgroup
                          cols={[
                            { key: 'description' },
                            { key: 'rate', pct: 14 },
                            { key: 'amount', pct: 22 },
                            { key: 'tax', pct: 22 },
                          ]}
                        />
                        <TableHead>
                          <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Tax</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx}>
                                PRSI Threshold (no PRSI on first €{calc.breakdown.prsi.threshold.toLocaleString()})
                              </Box>
                            </TableCell>
                            <TableCell align="right">0.0%</TableCell>
                            <TableCell align="right">{formatCurrency(calc.breakdown.prsi.threshold)}</TableCell>
                            <TableCell align="right">{formatCurrency(0)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx}>PRSI on income above threshold</Box>
                            </TableCell>
                            <TableCell align="right">{calc.breakdown.prsi.rate.toFixed(1)}%</TableCell>
                            <TableCell align="right">{formatCurrency(calc.breakdown.prsi.taxableAmount)}</TableCell>
                            <TableCell align="right">{formatCurrency(calc.breakdown.prsi.tax)}</TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 'bold' } }}>
                            <TableCell>Total PRSI</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">{formatCurrency(calc.breakdown.prsi.threshold + calc.breakdown.prsi.taxableAmount)}</TableCell>
                            <TableCell align="right">{formatCurrency(calc.breakdown.prsi.tax)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
                  </CardContent>
                </Card>

                {/* Gross to Net + Offset Tax via Pension - side by side */}
                <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                <Card sx={{ flex: '1 1 45%', minWidth: 320 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Gross to Net
                    </Typography>
                    {(() => {
                      const getPersonFamilyId = (p: Person | undefined) => p && ((p as any).family_id ?? (p as any).familyId);
                      const familyIncomes = isOmHe && effectiveScenarioData ? effectiveIncomes : incomes.filter(inc => getPersonFamilyId(inc.person) === calc.familyId);
                      let earnedHeidi = 0, earnedOmar = 0, rentalHeidi = 0, rentalOmar = 0;
                      familyIncomes.forEach(inc => {
                        const amt = inc.amount_yearly ?? 0;
                        const isRental = isRentalIncomeType(inc);
                        const isHeidi = inc.person?.name?.toLowerCase().includes('heidi');
                        const isOmar = inc.person?.name?.toLowerCase().includes('omar');
                        if (isRental) {
                          if (isHeidi) rentalHeidi += amt;
                          else if (isOmar) rentalOmar += amt;
                        } else {
                          if (isHeidi) earnedHeidi += amt;
                          else if (isOmar) earnedOmar += amt;
                        }
                      });
                      const pensionHeidi = effectivePensions.reduce((s, r) => s + r.heidi, 0);
                      const pensionOmar = effectivePensions.reduce((s, r) => s + r.omar, 0);
                      const totalPension = pensionHeidi + pensionOmar;
                      const pr = pensionReliefResult;
                      const reliefAmount = pr?.reliefAmount ?? Math.min(totalPension, calc.earnedIncome);
                      const payeRelief = pr?.payeRelief ?? 0;
                      const taxableIncome = calc.totalIncome - reliefAmount;
                      const taxCredits = (calc.breakdown.taxCreditsBreakdown ?? []).reduce((s, c) => s + c.amount, 0);
                      const payeBeforeCredits = calc.breakdown.payeBeforeCredits ?? calc.breakdown.payeBands.reduce((s, b) => s + b.tax, 0);
                      const netIncome = totalPension > 0 ? calc.netIncome - totalPension + payeRelief : calc.netIncome;
                      const Row = ({ label, heidi, omar, combined, color, bold }: { label: string; heidi: number | null; omar: number | null; combined: number; color?: string; bold?: boolean }) => (
                        <TableRow sx={{ '& td': bold ? { fontWeight: 'bold' } : undefined, ...(bold ? { borderTop: 2, borderColor: 'divider' } : {}) }}>
                          <TableCell>
                            <Box component="span" sx={fitTruncateSx} title={label}>{label}</Box>
                          </TableCell>
                          <TableCell align="right" sx={{ color: color || 'inherit' }}>{heidi != null ? formatCurrency(heidi) : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: color || 'inherit' }}>{omar != null ? formatCurrency(omar) : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: color || 'inherit' }}>{formatCurrency(combined)}</TableCell>
                          <TableCell align="right" sx={{ color: color || 'inherit' }}>{formatCurrency(combined / 12)}</TableCell>
                        </TableRow>
                      );
                      return (
                        <>
                          <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                          <Table size="small" sx={FIT_TABLE_SX}>
                            <FitColgroup
                              cols={equalFitCols(
                                ['line', 'heidi_pa', 'omar_pa', 'combined_pa', 'combined_pm'],
                                { flexKey: 'line', nonFlexTotal: 68 }
                              )}
                            />
                            <TableHead>
                              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Line</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Heidi{'\n'}per annum</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Omar{'\n'}per annum</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Combined{'\n'}per annum</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'pre-line' }}>Combined{'\n'}per month</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <Row label="Gross earned income" heidi={earnedHeidi} omar={earnedOmar} combined={calc.earnedIncome} />
                              <Row label="Gross rental income" heidi={rentalHeidi} omar={rentalOmar} combined={calc.rentalIncome} />
                              <Row label="Total Gross income" heidi={earnedHeidi + rentalHeidi} omar={earnedOmar + rentalOmar} combined={calc.totalIncome} />
                              <Row label="Personal Pension contribution" heidi={pensionHeidi} omar={pensionOmar} combined={totalPension} />
                              <Row label="Taxable income" heidi={null} omar={null} combined={taxableIncome} />
                              <Row label="Income tax" heidi={null} omar={null} combined={-payeBeforeCredits} color="error.main" />
                              <Row label="USC" heidi={null} omar={null} combined={-calc.uscTax} color="error.main" />
                              <Row label="PRSI" heidi={null} omar={null} combined={-calc.prsiTax} color="error.main" />
                              <Row label="Tax credits" heidi={null} omar={null} combined={taxCredits} color="success.main" />
                              <Row label="Net income" heidi={null} omar={null} combined={netIncome} bold />
                              <TableRow>
                                <TableCell>Effective tax rate</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">{calc.totalIncome > 0 ? `${((totalPension > 0 ? calc.totalTax - payeRelief : calc.totalTax) / calc.totalIncome * 100).toFixed(1)}%` : '—'}</TableCell>
                                <TableCell align="right">—</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {reliefAmount < totalPension && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            (Pension relief on earned income only, PAYE at marginal rate; age cap applies. €{formatCurrency(totalPension - reliefAmount)} not relievable)
                          </Typography>
                        )}

                        {/* Post-Net Expenses */}
                        {(() => {
                          // Backend returns scenario-specific loans; use all mortgages
                          const mortgageOrder = ['B&G', 'AIB', 'Pepper'];
                          const mortgageSortKey = (l: Loan) => {
                            const label = ((l.lender || l.loan_name || '').trim() || 'Mortgage').toLowerCase();
                            const idx = mortgageOrder.findIndex(m => label.includes(m.toLowerCase()));
                            return idx >= 0 ? idx : mortgageOrder.length;
                          };
                          const mortgageLoansList = effectiveLoans.filter((l: Loan) => l.loan_type === 'mortgage').sort((a, b) => mortgageSortKey(a) - mortgageSortKey(b));
                          const mortgagesMonthly = mortgageLoansList.reduce((s, l) => s + (l.monthly_payment ?? 0), 0);
                          const mortgagesAnnual = mortgagesMonthly * 12;
                          const disposableIncome = netIncome - mortgagesAnnual;
                          const getMortgageLabel = (l: Loan) => (l.lender || l.loan_name || 'Mortgage').trim() || 'Mortgage';
                          return (
                            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MortgageIcon fontSize="small" />
                                Post-Net Expenses
                              </Typography>
                              <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, maxWidth: 400 }}>
                                <Table size="small" sx={FIT_TABLE_SX}>
                                  <FitColgroup
                                    cols={[
                                      { key: 'line' },
                                      { key: 'per_annum', pct: 27 },
                                      { key: 'per_month', pct: 27 },
                                    ]}
                                  />
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Line</TableCell>
                                      <TableCell align="right">per annum</TableCell>
                                      <TableCell align="right">per month</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell>Net income</TableCell>
                                      <TableCell align="right">{formatCurrency(netIncome)}</TableCell>
                                      <TableCell align="right">{formatCurrency(netIncome / 12)}</TableCell>
                                    </TableRow>
                                    {mortgageLoansList.map((loan: Loan) => {
                                      const monthly = loan.monthly_payment ?? 0;
                                      const annual = monthly * 12;
                                      return (
                                        <TableRow key={loan.id}>
                                          <TableCell>
                                            <Box component="span" sx={fitTruncateSx} title={getMortgageLabel(loan)}>
                                              {getMortgageLabel(loan)}
                                            </Box>
                                          </TableCell>
                                          <TableCell align="right" sx={{ color: 'error.main' }}>-{formatCurrency(annual)}</TableCell>
                                          <TableCell align="right" sx={{ color: 'error.main' }}>-{formatCurrency(monthly)}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                    <TableRow sx={{ '& td': { fontWeight: 'bold' }, borderTop: 2, borderColor: 'divider' }}>
                                      <TableCell>Disposable income</TableCell>
                                      <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(disposableIncome)}</TableCell>
                                      <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(disposableIncome / 12)}</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          );
                        })()}
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Offset Tax via Pension */}
                <Card sx={{ flex: '1 1 45%', minWidth: 320 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SavingsIcon />
                      Offset Tax via Pension
                    </Typography>
                    {(() => {
                      const pr = pensionReliefResult;
                      if (!pr) return <Typography variant="body2" color="text.secondary">Loading pension data...</Typography>;
                      const totalPension = effectivePensions.reduce((s, r) => s + r.heidi + r.omar, 0);
                      const { maxRelievable, payeReliefAtMax: payeReliefMax, amountIn20Band, amountIn40Band } = pr;
                      const taxAtMaxRelievable = calc.totalTax - payeReliefMax;
                      const canEliminatePaye = payeReliefMax >= calc.payeTax && maxRelievable > 0;
                      let pensionToEliminatePaye = 0;
                      const agePct = pensionConfig?.ageBands?.[pensionAgeBand] ?? 0.25;
                      const maxByAge = pr.maxByAge ?? (agePct * Math.min(calc.earnedIncome, pensionConfig?.incomeCap ?? 115000));
                      if (canEliminatePaye) {
                        const cap = Math.min(calc.earnedIncome, maxByAge);
                        let reliefNeeded = calc.payeTax;
                        let p: number;
                        if (amountIn40Band * 0.40 >= reliefNeeded) {
                          p = reliefNeeded / 0.40;
                        } else {
                          p = amountIn40Band + (reliefNeeded - amountIn40Band * 0.40) / 0.20;
                        }
                        pensionToEliminatePaye = Math.min(Math.ceil(p), cap);
                      }
                      const in40AtMax = Math.min(maxRelievable, amountIn40Band);
                      const in20AtMax = Math.max(0, maxRelievable - amountIn40Band);
                      const agePctLabel = pensionAgeBand ? `${((pensionConfig?.ageBands?.[pensionAgeBand] ?? 0) * 100).toFixed(0)}%` : '';
                      const payePart = calc.payeTax;
                      const uscPart = calc.uscTax;
                      const prsiPart = calc.prsiTax;
                      const earnedCapped = Math.min(calc.earnedIncome, pensionConfig?.incomeCap ?? 115000);
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Income split */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Income split</Typography>
                            <Typography variant="body2" component="div">
                              <strong>Earned</strong> (PAYE, self-employment): {formatCurrency(calc.earnedIncome)}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                = Total income − rental; max contribution based on this
                              </Typography>
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                              <strong>Rental</strong>: {formatCurrency(calc.rentalIncome)}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                = Sum of rental income sources; doesn't increase max contribution, but relief effectively offsets tax on it (from 40% band first)
                              </Typography>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                              Total: {formatCurrency(calc.totalIncome)} = {formatCurrency(calc.earnedIncome)} + {formatCurrency(calc.rentalIncome)}
                            </Typography>
                          </Box>

                          {/* Current total tax breakdown */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Current total tax</Typography>
                            <Typography variant="body2" component="div">
                              PAYE: ({formatCurrency(payePart)}) + USC: ({formatCurrency(uscPart)}) + PRSI: ({formatCurrency(prsiPart)}) = ({formatCurrency(calc.totalTax)})
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              Pension relief reduces PAYE only; USC and PRSI unchanged
                            </Typography>
                          </Box>

                          {/* Max relievable with calculation */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Max relievable pension</Typography>
                            <Typography variant="body2" component="div" color="text.secondary">
                              The maximum you can contribute with tax relief is the lower of: your earned income, or {agePctLabel} of your earned income (capped at €115,000).
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                              Your earned income: {formatCurrency(calc.earnedIncome)}
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mt: 0.25 }}>
                              Age-based limit: {formatCurrency(earnedCapped)} × {agePctLabel} = {formatCurrency(maxByAge)}
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                              Max pension contribution (within these constraints): {formatCurrency(maxRelievable)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Relief on your current contribution ({formatCurrency(totalPension)}): {formatCurrency(pr.reliefAmount)}
                              {totalPension === 0 && ' — see below for savings if you contribute the max.'}
                            </Typography>
                          </Box>

                          {/* Tax band split */}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Combined income in tax bands</Typography>
                            <Typography variant="body2" component="div">
                              {formatCurrency(amountIn20Band)} at 20% + {formatCurrency(amountIn40Band)} at 40% = {formatCurrency(calc.totalIncome)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              Relief applied from 40% band first (top-slicing)
                            </Typography>
                          </Box>

                          {/* If you contribute max: relief by band and savings */}
                          {maxRelievable > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'action.hover' }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>If you contribute {formatCurrency(maxRelievable)}</Typography>
                              <Typography variant="body2" component="div">
                                Relief by band:
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ pl: 2, mt: 0.5 }}>
                                • {formatCurrency(in40AtMax)} from 40% band → {formatCurrency(in40AtMax * 0.40)} PAYE relief
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                                • {formatCurrency(in20AtMax)} from 20% band → {formatCurrency(in20AtMax * 0.20)} PAYE relief
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ mt: 1, fontWeight: 'bold' }}>
                                Total PAYE relief: {formatCurrency(payeReliefMax)}
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                                Tax after: {formatCurrency(calc.totalTax)} − {formatCurrency(payeReliefMax)} = <strong>{formatCurrency(taxAtMaxRelievable)}</strong>
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                USC ({formatCurrency(uscPart)}) and PRSI ({formatCurrency(prsiPart)}) unchanged. Your max contribution is based on earned income only, but the relief reduces total taxable income from the top (40% band first), so it effectively offsets tax on rental too.
                              </Typography>
                            </Paper>
                          )}

                          {canEliminatePaye ? (
                            <>
                              <Box>
                                <Typography variant="body2" color="text.secondary">Pension to eliminate PAYE</Typography>
                                <Typography variant="h6" color="success.main">{formatCurrency(pensionToEliminatePaye)}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {formatCurrency(pensionToEliminatePaye / 12)} per month
                                </Typography>
                              </Box>
                              {totalPension > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  You contribute {formatCurrency(totalPension)}. Add {formatCurrency(Math.max(0, pensionToEliminatePaye - totalPension))} more to eliminate PAYE.
                                </Typography>
                              )}
                            </>
                          ) : null}
                          {maxRelievable > 0 && calc.totalIncome > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Tax breakdown: before vs after max pension contribution</Typography>
                              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mt: 1 }}>Originally (before Pension contribution):</Typography>
                              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                                PAYE ({formatCurrency(payePart)}) + USC ({formatCurrency(uscPart)}) + PRSI ({formatCurrency(prsiPart)}) = {formatCurrency(calc.totalTax)} Total Tax bill (i.e. {(calc.totalTax / calc.totalIncome * 100).toFixed(1)}% effective tax rate).
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mt: 1.5 }}>After a pension contribution of {formatCurrency(maxRelievable)}:</Typography>
                              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                                PAYE ({formatCurrency(Math.max(0, payePart - payeReliefMax))}) + USC ({formatCurrency(uscPart)}) + PRSI ({formatCurrency(prsiPart)}) = {formatCurrency(taxAtMaxRelievable)} Total Tax bill (i.e. {(taxAtMaxRelievable / calc.totalIncome * 100).toFixed(1)}% effective tax rate).
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ mt: 1.5, fontWeight: 'bold', color: 'success.main' }}>
                                Pension Contribution Saves you:
                              </Typography>
                              <Typography variant="body2" component="div" sx={{ pl: 2, fontWeight: 'bold', color: 'success.main' }}>
                                {formatCurrency(payeReliefMax)} or {(payeReliefMax / calc.totalTax * 100).toFixed(1)}% of your tax bill.
                              </Typography>
                            </Paper>
                          )}

                          {/* Comprehensive commentary */}
                          <Paper variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: 'action.hover' }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>How pension relief works</Typography>
                            <Typography variant="caption" component="div" sx={{ display: 'block', mb: 1 }}>
                              <strong>Rules:</strong> Relief applies to <strong>earned income only</strong> (PAYE, self-employment). Rental income does not qualify for relief, but pension contributions reduce your <em>total</em> taxable income, so they effectively offset tax on rental too—up to the limit. <strong>No USC or PRSI relief</strong> on pension contributions (Revenue rules).
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ display: 'block', mb: 1 }}>
                              <strong>Max contribution:</strong> min(age % × min(earned, €115k), earned). Your earned: {formatCurrency(calc.earnedIncome)}; rental: {formatCurrency(calc.rentalIncome)}; total: {formatCurrency(calc.totalIncome)}. Age band ({agePctLabel} of min(earned, €115k)) = {formatCurrency(maxByAge)}. Max relievable = {formatCurrency(maxRelievable)}.
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ display: 'block', mb: 1 }}>
                              <strong>Upper band optimisation:</strong> Relief is applied at the highest rate first (40% band, then 20%). Your combined income splits as €{formatCurrency(amountIn20Band)} at 20% and €{formatCurrency(amountIn40Band)} at 40%. Max relievable ({formatCurrency(maxRelievable)}) reduces €{formatCurrency(in40AtMax)} from 40% band and €{formatCurrency(in20AtMax)} from 20% band → relief = €{formatCurrency(in40AtMax)}×40% + €{formatCurrency(in20AtMax)}×20% = €{formatCurrency(payeReliefMax)}.
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ display: 'block' }}>
                              {calc.rentalIncome > 0 && (
                                <>Rental (€{formatCurrency(calc.rentalIncome)}) remains taxable; pension reduces total taxable income from the top (40% band first), so it effectively offsets tax on rental up to the age cap.</>
                              )}
                              {calc.rentalIncome <= 0 && 'Relief applies at marginal rate (40% band first, then 20%).'}
                            </Typography>
                          </Paper>

                          {/* Recommendations from backend API */}
                          {recommendationsMap[String(calc.familyId)] && (
                            <Paper variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: 'action.hover' }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                {recommendationsMap[String(calc.familyId)].title}
                              </Typography>
                              {(recommendationsMap[String(calc.familyId)].paragraphs || []).map((p, i) => (
                                <Typography key={i} variant="body2" component="div" sx={{ mt: i === 0 ? 1 : 1 }}>
                                  {p}
                                </Typography>
                              ))}
                              {recommendationsMap[String(calc.familyId)].savingAmount != null && recommendationsMap[String(calc.familyId)].savingAmount! > 0 && (
                                <Typography variant="body2" component="div" sx={{ mt: 1, color: 'success.main', fontWeight: 'bold' }}>
                                  Tax saving: {formatCurrency(recommendationsMap[String(calc.familyId)].savingAmount!)} per year.
                                </Typography>
                              )}
                            </Paper>
                          )}
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
                </Box>
                </>
                  );
                })()
                )}
              </Box>
            )}
          </div>
        ))}

        {/* Add Income Source Dialog */}
        <Dialog open={incomeAddOpen} onClose={() => { setIncomeAddOpen(false); setIncomeAddFamilyId(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Add Income Source</DialogTitle>
          <form onSubmit={(e) => { e.preventDefault(); handleIncomeAddSubmit(); }}>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField label="Heidi per annum" type="number" value={incomeAddForm.heidiAmount || ''}
                  onChange={(e) => setIncomeAddForm(prev => ({ ...prev, heidiAmount: parseFloat(e.target.value) || 0 }))}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                <TextField label="Omar per annum" type="number" value={incomeAddForm.omarAmount || ''}
                  onChange={(e) => setIncomeAddForm(prev => ({ ...prev, omarAmount: parseFloat(e.target.value) || 0 }))}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                <TextField label="Combined per annum" type="number" value={(incomeAddForm.heidiAmount + incomeAddForm.omarAmount) || ''}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} disabled />
                <TextField label="Combined per month" type="number" value={((incomeAddForm.heidiAmount + incomeAddForm.omarAmount) / 12) || ''}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} disabled />
                <FormControl size="small" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={incomeAddForm.type} label="Type" onChange={(e) => setIncomeAddForm(prev => ({ ...prev, type: e.target.value }))}>
                    <MenuItem value="Contractor">Contractor</MenuItem>
                    <MenuItem value="Sole Trader">Sole Trader</MenuItem>
                    <MenuItem value="PAYE">PAYE</MenuItem>
                    <MenuItem value="Rental Income">Rental Income</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select value={incomeAddForm.sourceEmployer} label="Source" onChange={(e) => setIncomeAddForm(prev => ({ ...prev, sourceEmployer: e.target.value }))}>
                    {SOURCE_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Internal / External</InputLabel>
                  <Select value={incomeAddForm.internalExternal} label="Internal / External" onChange={(e) => setIncomeAddForm(prev => ({ ...prev, internalExternal: e.target.value }))}>
                    <MenuItem value="Internal">Internal</MenuItem>
                    <MenuItem value="External">External</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Details" value={incomeAddForm.details || ''}
                  onChange={(e) => setIncomeAddForm(prev => ({ ...prev, details: e.target.value }))}
                  fullWidth size="small" placeholder="e.g. Lucena/Rotunda Gross" />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setIncomeAddOpen(false); setIncomeAddFamilyId(null); }} disabled={incomeSourceActionLoading}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={incomeSourceActionLoading}>
                {incomeSourceActionLoading ? 'Adding...' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Add Pension Dialog */}
        <Dialog open={pensionAddOpen} onClose={() => setPensionAddOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Pension</DialogTitle>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!pensionAddForm.name.trim()) return;
            const scenarioIdForPension = effectiveScenarioData?.scenarioId ?? (currentCalc && scenarioDataByFamily[currentCalc.familyId]?.scenarioId);
            if (scenarioIdForPension && currentCalc) {
              try {
                setPensionActionLoading(true);
                await api.post(`/api/tax-scenario/${scenarioIdForPension}/pensions`, {
                  name: pensionAddForm.name.trim(),
                  heidi: pensionAddForm.heidi,
                  omar: pensionAddForm.omar,
                  totalContributions: pensionAddForm.totalContributions,
                  currentValue: pensionAddForm.currentValue
                });
                if (showScenarioTabs) await fetchScenarioData(currentCalc.familyId, scenarioTab);
                else {
                  const r = await api.get('/api/tax-scenario/', { params: { family_id: currentCalc.familyId, scenario: 1 } });
                  setScenarioDataByFamily(prev => ({
                    ...prev,
                    [currentCalc.familyId]: {
                      pensions: (r.data.pensions || []).map((p: any) => ({
                        id: p.id, name: p.name, heidi: p.heidi ?? 0, omar: p.omar ?? 0,
                        totalContributions: p.totalContributions ?? p.total_contributions ?? 0,
                        currentValue: p.currentValue ?? p.current_value ?? 0
                      })),
                      scenarioId: r.data.scenario?.id
                    }
                  }));
                }
                setPensionAddOpen(false);
                setPensionAddForm({ name: '', heidi: 0, omar: 0, totalContributions: 0, currentValue: 0 });
                fetchTaxCalculations();
              } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to add pension');
              } finally {
                setPensionActionLoading(false);
              }
            }
          }}>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField label="Pension name" value={pensionAddForm.name} onChange={(e) => setPensionAddForm(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth size="small" placeholder="e.g. Aviva PRSA Pension" required />
                <TextField label="Heidi contributions this year (€)" type="number" value={pensionAddForm.heidi || ''}
                  onChange={(e) => setPensionAddForm(prev => ({ ...prev, heidi: parseFloat(e.target.value) || 0 }))}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                <TextField label="Omar contributions this year (€)" type="number" value={pensionAddForm.omar || ''}
                  onChange={(e) => setPensionAddForm(prev => ({ ...prev, omar: parseFloat(e.target.value) || 0 }))}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                <TextField label="Total contributions to date (€)" type="number" value={pensionAddForm.totalContributions || ''}
                  onChange={(e) => setPensionAddForm(prev => ({ ...prev, totalContributions: parseFloat(e.target.value) || 0 }))}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                  helperText="All contributions made over all years" />
                <TextField label="Current value (€)" type="number" value={pensionAddForm.currentValue || ''}
                  onChange={(e) => setPensionAddForm(prev => ({ ...prev, currentValue: parseFloat(e.target.value) || 0 }))}
                  fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                  helperText="How much the pension is currently worth" />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPensionAddOpen(false)}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={!pensionAddForm.name.trim() || pensionActionLoading}>
                {pensionActionLoading ? 'Adding...' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Edit Pension Dialog */}
        <Dialog open={pensionEditOpen} onClose={() => { setPensionEditOpen(false); setPensionEditIdx(null); setPensionEditId(null); setPensionEditForm(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Pension</DialogTitle>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!pensionEditForm) return;
            const scenarioIdForPensionEdit = effectiveScenarioData?.scenarioId ?? (currentCalc && scenarioDataByFamily[currentCalc.familyId]?.scenarioId);
            if (scenarioIdForPensionEdit && currentCalc && pensionEditId) {
              try {
                setPensionActionLoading(true);
                await api.put(`/api/tax-scenario/${scenarioIdForPensionEdit}/pensions`, {
                  id: pensionEditId,
                  name: pensionEditForm.name.trim(),
                  heidi: pensionEditForm.heidi,
                  omar: pensionEditForm.omar,
                  totalContributions: pensionEditForm.totalContributions,
                  currentValue: pensionEditForm.currentValue
                });
                if (showScenarioTabs) await fetchScenarioData(currentCalc.familyId, scenarioTab);
                else {
                  const r = await api.get('/api/tax-scenario/', { params: { family_id: currentCalc.familyId, scenario: 1 } });
                  setScenarioDataByFamily(prev => ({
                    ...prev,
                    [currentCalc.familyId]: {
                      pensions: (r.data.pensions || []).map((p: any) => ({
                        id: p.id, name: p.name, heidi: p.heidi ?? 0, omar: p.omar ?? 0,
                        totalContributions: p.totalContributions ?? p.total_contributions ?? 0,
                        currentValue: p.currentValue ?? p.current_value ?? 0
                      })),
                      scenarioId: r.data.scenario?.id
                    }
                  }));
                }
                setPensionEditOpen(false);
                setPensionEditIdx(null);
                setPensionEditId(null);
                setPensionEditForm(null);
                fetchTaxCalculations();
              } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to update pension');
              } finally {
                setPensionActionLoading(false);
              }
            }
          }}>
            <DialogContent>
              {pensionEditForm && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField label="Pension name" value={pensionEditForm.name} onChange={(e) => setPensionEditForm(prev => prev ? { ...prev, name: e.target.value } : null)}
                    fullWidth size="small" required />
                  <TextField label="Heidi contributions this year (€)" type="number" value={pensionEditForm.heidi || ''}
                    onChange={(e) => setPensionEditForm(prev => prev ? { ...prev, heidi: parseFloat(e.target.value) || 0 } : null)}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                  <TextField label="Omar contributions this year (€)" type="number" value={pensionEditForm.omar || ''}
                    onChange={(e) => setPensionEditForm(prev => prev ? { ...prev, omar: parseFloat(e.target.value) || 0 } : null)}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                  <TextField label="Total contributions to date (€)" type="number" value={pensionEditForm.totalContributions || ''}
                    onChange={(e) => setPensionEditForm(prev => prev ? { ...prev, totalContributions: parseFloat(e.target.value) || 0 } : null)}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                    helperText="All contributions made over all years" />
                  <TextField label="Current value (€)" type="number" value={pensionEditForm.currentValue || ''}
                    onChange={(e) => setPensionEditForm(prev => prev ? { ...prev, currentValue: parseFloat(e.target.value) || 0 } : null)}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                    helperText="How much the pension is currently worth" />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setPensionEditOpen(false); setPensionEditIdx(null); setPensionEditId(null); setPensionEditForm(null); }}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={!pensionEditForm?.name.trim() || pensionActionLoading}>
                {pensionActionLoading ? 'Updating...' : 'Update'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Delete Pension Confirmation Dialog */}
        <Dialog open={pensionDeleteOpen} onClose={() => { setPensionDeleteOpen(false); setPensionDeleteIdx(null); setPensionDeleteId(null); }}>
          <DialogTitle>Delete Pension</DialogTitle>
          <DialogContent>
            <Typography>
              Delete {(pensionDeleteIdx !== null && ((effectiveScenarioData?.pensions?.[pensionDeleteIdx] || (currentCalc && scenarioDataByFamily[currentCalc.familyId]?.pensions?.[pensionDeleteIdx]))?.name)) || 'this pension'}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setPensionDeleteOpen(false); setPensionDeleteIdx(null); setPensionDeleteId(null); }}>Cancel</Button>
            <Button onClick={async () => {
              if (pensionDeleteIdx === null) return;
              const scenarioIdForPensionDel = effectiveScenarioData?.scenarioId ?? (currentCalc && scenarioDataByFamily[currentCalc.familyId]?.scenarioId);
              if (scenarioIdForPensionDel && currentCalc && pensionDeleteId) {
                try {
                  setPensionActionLoading(true);
                  await api.delete(`/api/tax-scenario/${scenarioIdForPensionDel}/pensions?id=${pensionDeleteId}`);
                  if (showScenarioTabs) await fetchScenarioData(currentCalc.familyId, scenarioTab);
                  else {
                    const r = await api.get('/api/tax-scenario/', { params: { family_id: currentCalc.familyId, scenario: 1 } });
                    setScenarioDataByFamily(prev => ({
                      ...prev,
                      [currentCalc.familyId]: {
                        pensions: (r.data.pensions || []).map((p: any) => ({
                          id: p.id, name: p.name, heidi: p.heidi ?? 0, omar: p.omar ?? 0,
                          totalContributions: p.totalContributions ?? p.total_contributions ?? 0,
                          currentValue: p.currentValue ?? p.current_value ?? 0
                        })),
                        scenarioId: r.data.scenario?.id
                      }
                    }));
                  }
                  fetchTaxCalculations();
                } catch (err: any) {
                  setError(err.response?.data?.error || 'Failed to delete pension');
                } finally {
                  setPensionActionLoading(false);
                }
              }
              setPensionDeleteOpen(false);
              setPensionDeleteIdx(null);
              setPensionDeleteId(null);
            }} color="error" variant="contained" disabled={pensionActionLoading}>
              {pensionActionLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Income Source Confirmation Dialog */}
        <Dialog open={incomeSourceDeleteOpen} onClose={() => !incomeSourceActionLoading && (setIncomeSourceDeleteOpen(false), setIncomeToDeleteRecords([]))}>
          <DialogTitle>Delete Income Source</DialogTitle>
          <DialogContent>
            <Typography>
              Delete {incomeToDeleteRecords.length} income record{incomeToDeleteRecords.length !== 1 ? 's' : ''}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setIncomeSourceDeleteOpen(false); setIncomeToDeleteRecords([]); }} disabled={incomeSourceActionLoading}>Cancel</Button>
            <Button onClick={handleIncomeSourceDelete} color="error" variant="contained" disabled={incomeSourceActionLoading}>
              {incomeSourceActionLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Income Source Dialog */}
        <Dialog open={incomeSourceEditOpen} onClose={() => { setIncomeSourceEditOpen(false); setEditingIncomeRecords([]); setIncomeSourceEditForm(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Income Source</DialogTitle>
          <form onSubmit={(e) => { e.preventDefault(); handleIncomeSourceEditSubmit(); }}>
            <DialogContent>
              {incomeSourceEditForm && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField label="Heidi per annum" type="number" value={incomeSourceEditForm.heidiAmount || ''}
                    onChange={(e) => setIncomeSourceEditForm(prev => prev ? { ...prev, heidiAmount: parseFloat(e.target.value) || 0 } : null)}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                  <TextField label="Omar per annum" type="number" value={incomeSourceEditForm.omarAmount || ''}
                    onChange={(e) => setIncomeSourceEditForm(prev => prev ? { ...prev, omarAmount: parseFloat(e.target.value) || 0 } : null)}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
                  <TextField label="Combined per annum" type="number" value={(incomeSourceEditForm.heidiAmount + incomeSourceEditForm.omarAmount) || ''}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} disabled />
                  <TextField label="Combined per month" type="number" value={((incomeSourceEditForm.heidiAmount + incomeSourceEditForm.omarAmount) / 12) || ''}
                    fullWidth size="small" InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} disabled />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select value={incomeSourceEditForm.type} label="Type"
                      onChange={(e) => setIncomeSourceEditForm(prev => prev ? { ...prev, type: e.target.value } : null)}>
                      <MenuItem value="Contractor">Contractor</MenuItem>
                      <MenuItem value="Sole Trader">Sole Trader</MenuItem>
                      <MenuItem value="PAYE">PAYE</MenuItem>
                      <MenuItem value="Rental Income">Rental Income</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select value={incomeSourceEditForm.sourceEmployer} label="Source"
                      onChange={(e) => setIncomeSourceEditForm(prev => prev ? { ...prev, sourceEmployer: e.target.value } : null)}>
                      {SOURCE_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Internal / External</InputLabel>
                    <Select value={incomeSourceEditForm.internalExternal} label="Internal / External"
                      onChange={(e) => setIncomeSourceEditForm(prev => prev ? { ...prev, internalExternal: e.target.value } : null)}>
                      <MenuItem value="Internal">Internal</MenuItem>
                      <MenuItem value="External">External</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Details" value={incomeSourceEditForm.details || ''}
                    onChange={(e) => setIncomeSourceEditForm(prev => prev ? { ...prev, details: e.target.value } : null)}
                    fullWidth size="small" />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setIncomeSourceEditOpen(false); setEditingIncomeRecords([]); setIncomeSourceEditForm(null); }} disabled={incomeSourceActionLoading}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={incomeSourceActionLoading}>
                {incomeSourceActionLoading ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Edit Income Dialog */}
        <Dialog open={incomeEditOpen} onClose={handleIncomeEditClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Income for {editingPerson?.person.name}</DialogTitle>
          <form onSubmit={handleIncomeSubmit}>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <ToggleButtonGroup
                  value={incomeEditMode}
                  exclusive
                  onChange={(_, newMode) => newMode && setIncomeEditMode(newMode)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="monthly">Edit Monthly Amounts</ToggleButton>
                  <ToggleButton value="yearly">Edit Yearly Amounts</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label="PAYE Internal"
                    type="number"
                    value={incomeFormData.payeInternal}
                    onChange={(e) => setIncomeFormData({ ...incomeFormData, payeInternal: e.target.value })}
                    fullWidth
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                    helperText={incomeEditMode === 'monthly' ? 'Monthly amount' : 'Yearly amount'}
                  />
                  <TextField
                    label="PAYE External"
                    type="number"
                    value={incomeFormData.payeExternal}
                    onChange={(e) => setIncomeFormData({ ...incomeFormData, payeExternal: e.target.value })}
                    fullWidth
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                    helperText={incomeEditMode === 'monthly' ? 'Monthly amount' : 'Yearly amount'}
                  />
                  <TextField
                    label="Other Internal"
                    type="number"
                    value={incomeFormData.otherInternal}
                    onChange={(e) => setIncomeFormData({ ...incomeFormData, otherInternal: e.target.value })}
                    fullWidth
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                    helperText={incomeEditMode === 'monthly' ? 'Monthly amount' : 'Yearly amount'}
                  />
                  <TextField
                    label="Other External"
                    type="number"
                    value={incomeFormData.otherExternal}
                    onChange={(e) => setIncomeFormData({ ...incomeFormData, otherExternal: e.target.value })}
                    fullWidth
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }}
                    helperText={incomeEditMode === 'monthly' ? 'Monthly amount' : 'Yearly amount'}
                  />
                </Box>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2">
                    Total {incomeEditMode === 'monthly' ? 'Monthly' : 'Yearly'} Income: {formatCurrency(
                      (parseFloat(incomeFormData.payeInternal) || 0) +
                      (parseFloat(incomeFormData.payeExternal) || 0) +
                      (parseFloat(incomeFormData.otherInternal) || 0) +
                      (parseFloat(incomeFormData.otherExternal) || 0)
                    )}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleIncomeEditClose} disabled={incomeSubmitting}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={incomeSubmitting}>
                {incomeSubmitting ? 'Updating...' : 'Update Income'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Scenario Description Edit Modal */}
        <Dialog
          open={scenarioDescEditOpen}
          onClose={() => setScenarioDescEditOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Scenario Description</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              placeholder="Add a description for this scenario..."
              value={scenarioDescEditValue}
              onChange={(e) => setScenarioDescEditValue(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScenarioDescEditOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!effectiveScenarioData?.scenarioId || !currentCalc?.familyId) return;
                setScenarioDescSaving(true);
                try {
                  await api.put(`/api/tax-scenario/${effectiveScenarioData.scenarioId}`, {
                    description: scenarioDescEditValue
                  });
                  if (scenarioTab === 1) setScenarioData1(prev => prev ? { ...prev, description: scenarioDescEditValue } : null);
                  else if (scenarioTab === 2) setScenarioData2(prev => prev ? { ...prev, description: scenarioDescEditValue } : null);
                  setScenarioDescEditOpen(false);
                } catch (err: any) {
                  console.error('Failed to save scenario description:', err);
                } finally {
                  setScenarioDescSaving(false);
                }
              }}
              disabled={scenarioDescSaving}
            >
              {scenarioDescSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Irish Tax Information Modal */}
        <Dialog
          open={taxInfoModalOpen}
          onClose={() => setTaxInfoModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Irish Tax Information</DialogTitle>
          <DialogContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={taxInfoTab} onChange={(e, newValue) => setTaxInfoTab(newValue)}>
                <Tab label="2026" />
                <Tab label="2025" />
                <Tab label="2024" />
              </Tabs>
            </Box>
            
            {taxInfoTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>PAYE Tax Rates 2026</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Single: 20% up to €44,000, then 40%<br/>
                      • Married (one earner): 20% up to €53,000, then 40%<br/>
                      • Married (two earners): Primary €53,000 + secondary €35,000 at 20% (€88,000 combined), then 40%<br/>
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>USC Rates 2026</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • 0.5% up to €12,012<br/>
                      • 2% on €12,012 - €28,700<br/>
                      • 3% on €28,700 - €70,044<br/>
                      • 8% above €70,044
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>PRSI (Pay Related Social Insurance) 2026</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Employee rate: 4.2%<br/>
                      • Threshold: €13,100 (no PRSI on first €13,100)<br/>
                      • Applied to income above threshold
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Tax Credits 2026</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Single Person: €2,000 (Personal + PAYE)<br/>
                      • Married (one earner): €6,000 (Married Personal €4,000 + PAYE €2,000)<br/>
                      • Married (both PAYE): €8,000 (Married Personal €4,000 + PAYE €2,000×2)<br/>
                      • Old Age Pensioner: €2,000
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
            
            {taxInfoTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>PAYE Tax Rates 2025</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Single: 20% up to €44,000, then 40%<br/>
                      • Married (one earner): 20% up to €53,000, then 40%<br/>
                      • Married (two earners): Primary €53,000 + secondary €35,000 at 20% (€88,000 combined), then 40%<br/>
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>USC Rates 2025</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • 0.5% up to €12,012<br/>
                      • 2% on €12,012 - €25,760<br/>
                      • 4.5% on €25,760 - €70,044<br/>
                      • 8% above €70,044
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>PRSI (Pay Related Social Insurance) 2025</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Employee rate: 4%<br/>
                      • Threshold: €13,100 (no PRSI on first €13,100)<br/>
                      • Applied to income above threshold
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Tax Credits 2025</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Single Person: €2,000 (Personal + PAYE)<br/>
                      • Married (one earner): €6,000 (Married Personal €4,000 + PAYE €2,000)<br/>
                      • Married (both PAYE): €8,000 (Married Personal €4,000 + PAYE €2,000×2)<br/>
                      • Old Age Pensioner: €2,000
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {taxInfoTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>PAYE Tax Rates 2024</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Single: 20% up to €42,000, then 40%<br/>
                      • Married (one earner): 20% up to €51,000, then 40%<br/>
                      • Married (two earners, max transfer): 20% up to €84,000, then 40%<br/>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                        (Max transfer: €33,000)
                      </Typography>
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>USC Rates 2024</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • 0.5% up to €12,012<br/>
                      • 2% on €12,012 - €25,760<br/>
                      • 4.5% on €25,760 - €70,044<br/>
                      • 8% above €70,044
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>PRSI (Pay Related Social Insurance) 2024</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Employee rate: 4%<br/>
                      • Threshold: €13,100 (no PRSI on first €13,100)<br/>
                      • Applied to income above threshold
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Tax Credits 2024</Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Single Person: €1,875 (Personal + PAYE)<br/>
                      • Married (one earner): €5,625 (Married Personal €3,750 + PAYE €1,875)<br/>
                      • Married (both PAYE): €7,500 (Married Personal €3,750 + PAYE €1,875×2)<br/>
                      • Old Age Pensioner: €1,875
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
            
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Source: <a href="https://www.revenue.ie/en/personal-tax-credits-reliefs-and-exemptions/tax-relief-charts/index.aspx" target="_blank" rel="noopener noreferrer">Revenue.ie - Tax rates, bands and reliefs</a>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTaxInfoModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
